import React, { forwardRef, useRef, useState, useEffect, useImperativeHandle, useCallback, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { createRoot } from 'react-dom/client'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
// Removed quill-table-up due to compatibility issues
import EmojiPicker from './EmojiPicker'
import DrawingModal from './DrawingModal'
import AudioRecordingModal from './AudioRecordingModal'
import ColorPickerModal from './ColorPickerModal'
import PickerModal from './PickerModal'
import ImageResizeHandles from './ImageResizeHandles'
import ImageResizeModal from './ImageResizeModal'
import NoteLinkModal from './NoteLinkModal'
import { NoteLinkBlot } from './NoteMentionModule'
import { uploadFile, uploadAttachment } from '../../services/filesAPI'
import { processImageForEditor, getBase64SizeKB } from '../../utils/imageCompression'
import { uploadImageFile, getImageBase64, extractFileIdFromUrl } from '../../services/imageUploadService'
import { authAPI } from '../../services/api'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useFolders } from '../../contexts/FoldersContext'
import { useTabs } from '../../contexts/TabsContext'
import localStorageService from '../../services/localStorageService'
import toast from 'react-hot-toast'
import aiIcon from '../../assets/ai-text-generation.png'

// Register custom Quill blot for note links
Quill.register(NoteLinkBlot)

// Simple table implementation using Quill's built-in capabilities
const TableBlot = Quill.import('blots/block/embed')

class SimpleTableBlot extends TableBlot {
  static blotName = 'table'
  static tagName = 'table'
  static className = 'ql-table'

  static value(node) {
    const rows = node.querySelectorAll('tr').length
    const cols = node.querySelectorAll('tr:first-child td').length
    return { rows, cols }
  }

  static formats(node) {
    // Return the table format to ensure proper recognition
    return 'table'
  }

  // Override the static method to handle HTML parsing
  static match(node) {
    return node.tagName === 'TABLE' && node.classList.contains('ql-table')
  }

  // Override the static method to create from existing HTML
  static createFromHTML(html) {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    const table = tempDiv.querySelector('table')
    
    if (table) {
      // Ensure proper styling and attributes
      table.setAttribute('border', '1')
      table.setAttribute('cellpadding', '0')
      table.setAttribute('cellspacing', '0')
      table.style.borderCollapse = 'collapse'
      table.style.width = '100%'
      table.style.position = 'relative'
      table.classList.add('ql-table')
      
      // Add event listeners to existing cells
      this.addEventListenersToTable(table)
      return table
    }
    
    return null
  }

  // Override the create method to handle existing HTML content
  static create(value) {
    let node
    
    // If value is a string (existing HTML), parse it
    if (typeof value === 'string') {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = value
      node = tempDiv.querySelector('table')
      
      if (node) {
        // Ensure proper styling and attributes
        node.setAttribute('border', '1')
        node.setAttribute('cellpadding', '0')
        node.setAttribute('cellspacing', '0')
        node.style.borderCollapse = 'collapse'
        node.style.width = '100%'
        node.style.position = 'relative'
        
        // Add event listeners to existing cells
        this.addEventListenersToTable(node)
        return node
      }
    }
    
    // Create new table if no existing HTML or parsing failed
    node = super.create()
    node.setAttribute('border', '1')
    node.setAttribute('cellpadding', '0')
    node.setAttribute('cellspacing', '0')
    node.style.borderCollapse = 'collapse'
    node.style.width = '100%'
    node.style.position = 'relative'
    
    // Create table structure
    const tbody = document.createElement('tbody')
    const rows = value?.rows || 3
    const cols = value?.cols || 3
    
    for (let i = 0; i < rows; i++) {
      const row = document.createElement('tr')
      row.dataset.rowIndex = i
      for (let j = 0; j < cols; j++) {
        const cell = document.createElement('td')
        cell.style.border = '1px solid #ccc'
        cell.style.padding = '8px'
        cell.style.minWidth = '50px'
        cell.style.minHeight = '30px'
        cell.style.position = 'relative'
        cell.dataset.colIndex = j
        cell.innerHTML = '<br>' // Empty cell with line break
        
        row.appendChild(cell)
      }
      tbody.appendChild(row)
    }
    
    node.appendChild(tbody)
    
    // Add event listeners
    this.addEventListenersToTable(node)
    
    return node
  }

  static addEventListenersToTable(table) {
    const cells = table.querySelectorAll('td')
    
    cells.forEach((cell, index) => {
      // Skip if already has event listeners
      if (cell.dataset.hasListeners === 'true') return
      
      // Add context menu functionality for desktop and mobile
      cell.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        this.showContextMenu(e, cell, table)
      })
      
      // Add mobile touch support
      let touchStartTime = 0
      let touchTimer = null
      
      cell.addEventListener('touchstart', (e) => {
        touchStartTime = Date.now()
        touchTimer = setTimeout(() => {
          // Long press detected
          e.preventDefault()
          this.showContextMenu(e, cell, table)
        }, 500) // 500ms long press
      })
      
      cell.addEventListener('touchend', (e) => {
        if (touchTimer) {
          clearTimeout(touchTimer)
          touchTimer = null
        }
      })
      
      cell.addEventListener('touchmove', (e) => {
        if (touchTimer) {
          clearTimeout(touchTimer)
          touchTimer = null
        }
      })
      
      // Add visual feedback for mobile
      cell.addEventListener('touchstart', (e) => {
        cell.style.backgroundColor = '#f0f0f0'
      })
      
      cell.addEventListener('touchend', (e) => {
        setTimeout(() => {
          cell.style.backgroundColor = ''
        }, 100)
      })
      
      // Mark as having listeners
      cell.dataset.hasListeners = 'true'
    })
    
    // Add table-level event listeners
    table.addEventListener('click', (e) => {
      if (e.target.tagName === 'TD') {
        this.hideContextMenu()
      }
    })
  }

  static showContextMenu(event, cell, table) {
    // Remove existing context menu
    this.hideContextMenu()
    
    const menu = document.createElement('div')
    menu.className = 'table-context-menu'
    menu.style.cssText = `
      position: fixed;
      background: white;
      border: 1px solid #ccc;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      z-index: 1000;
      padding: 8px 0;
      min-width: 160px;
      font-size: 16px;
      user-select: none;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
    `
    
    const rowIndex = parseInt(cell.parentElement.dataset.rowIndex)
    const colIndex = parseInt(cell.dataset.colIndex)
    const totalRows = table.querySelectorAll('tr').length
    const totalCols = table.querySelectorAll('tr:first-child td').length
    
    // Add row operations
    const addRowAbove = this.createMenuItem('Add Row Above', () => {
      this.addRow(table, rowIndex)
      this.hideContextMenu()
    })
    
    const addRowBelow = this.createMenuItem('Add Row Below', () => {
      this.addRow(table, rowIndex + 1)
      this.hideContextMenu()
    })
    
    const deleteRow = this.createMenuItem('Delete Row', () => {
      if (totalRows > 1) {
        this.deleteRow(table, rowIndex)
      }
      this.hideContextMenu()
    }, totalRows <= 1)
    
    // Add column operations
    const addColLeft = this.createMenuItem('Add Column Left', () => {
      this.addColumn(table, colIndex)
      this.hideContextMenu()
    })
    
    const addColRight = this.createMenuItem('Add Column Right', () => {
      this.addColumn(table, colIndex + 1)
      this.hideContextMenu()
    })
    
    const deleteCol = this.createMenuItem('Delete Column', () => {
      if (totalCols > 1) {
        this.deleteColumn(table, colIndex)
      }
      this.hideContextMenu()
    }, totalCols <= 1)
    
    // Add separator
    const separator = document.createElement('div')
    separator.style.cssText = 'height: 1px; background: #eee; margin: 4px 0;'
    
    // Add delete table option
    const deleteTable = this.createMenuItem('Delete Table', () => {
      table.remove()
      this.hideContextMenu()
    })
    
    menu.appendChild(addRowAbove)
    menu.appendChild(addRowBelow)
    menu.appendChild(deleteRow)
    menu.appendChild(separator.cloneNode())
    menu.appendChild(addColLeft)
    menu.appendChild(addColRight)
    menu.appendChild(deleteCol)
    menu.appendChild(separator.cloneNode())
    menu.appendChild(deleteTable)
    
    document.body.appendChild(menu)
    
    // Position menu - handle both mouse and touch events
    let clientX, clientY
    
    if (event.type === 'touchstart' || event.type === 'touchend') {
      // For touch events, use the first touch point
      const touch = event.touches?.[0] || event.changedTouches?.[0]
      clientX = touch?.clientX || event.clientX
      clientY = touch?.clientY || event.clientY
          } else {
      // For mouse events
      clientX = event.clientX
      clientY = event.clientY
    }
    
    // Ensure menu stays within viewport
    const menuWidth = 150
    const menuHeight = 200
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    
    let left = clientX
    let top = clientY
    
    // Adjust horizontal position if menu would go off-screen
    if (left + menuWidth > viewportWidth) {
      left = viewportWidth - menuWidth - 10
    }
    
    // Adjust vertical position if menu would go off-screen
    if (top + menuHeight > viewportHeight) {
      top = viewportHeight - menuHeight - 10
    }
    
    menu.style.left = `${Math.max(10, left)}px`
    menu.style.top = `${Math.max(10, top)}px`
    
    // Store reference for cleanup
    this.currentContextMenu = menu
    
    // Add click-outside handler to close menu
    const closeMenuOnClickOutside = (e) => {
      if (!menu.contains(e.target)) {
        this.hideContextMenu()
        document.removeEventListener('click', closeMenuOnClickOutside)
        document.removeEventListener('touchstart', closeMenuOnClickOutside)
      }
    }
    
    // Use setTimeout to avoid immediate closure
    setTimeout(() => {
      document.addEventListener('click', closeMenuOnClickOutside)
      document.addEventListener('touchstart', closeMenuOnClickOutside)
    }, 100)
  }

  static createMenuItem(text, onClick, disabled = false) {
    const item = document.createElement('div')
    item.textContent = text
    item.style.cssText = `
      padding: 12px 20px;
      cursor: pointer;
      font-size: 16px;
      color: ${disabled ? '#999' : '#333'};
      background: ${disabled ? '#f5f5f5' : 'transparent'};
      transition: background-color 0.2s ease;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    `
    
    if (!disabled) {
      item.addEventListener('click', onClick)
      item.addEventListener('mouseenter', () => {
        item.style.backgroundColor = '#f0f0f0'
      })
      item.addEventListener('mouseleave', () => {
        item.style.backgroundColor = 'transparent'
      })
      
      // Add touch support for mobile
      item.addEventListener('touchstart', () => {
        item.style.backgroundColor = '#e0e0e0'
      })
      item.addEventListener('touchend', () => {
        setTimeout(() => {
          item.style.backgroundColor = 'transparent'
        }, 150)
      })
    }
    
    return item
  }

  static hideContextMenu() {
    if (this.currentContextMenu) {
      this.currentContextMenu.remove()
      this.currentContextMenu = null
    }
  }

  static addRow(table, index) {
    const tbody = table.querySelector('tbody')
    const rows = tbody.querySelectorAll('tr')
    const cols = rows[0] ? rows[0].querySelectorAll('td').length : 3
    
    const newRow = document.createElement('tr')
    newRow.dataset.rowIndex = index
    
    for (let i = 0; i < cols; i++) {
      const cell = document.createElement('td')
      cell.style.border = '1px solid #ccc'
      cell.style.padding = '8px'
      cell.style.minWidth = '50px'
      cell.style.minHeight = '30px'
      cell.style.position = 'relative'
      cell.dataset.colIndex = i
      cell.innerHTML = '<br>'
      
      newRow.appendChild(cell)
    }
    
    if (index >= rows.length) {
      tbody.appendChild(newRow)
        } else {
      tbody.insertBefore(newRow, rows[index])
    }
    
    // Update row indices
    this.updateRowIndices(table)
    
    // Add event listeners to new cells
    this.addEventListenersToTable(table)
  }

  static deleteRow(table, index) {
    const tbody = table.querySelector('tbody')
    const rows = tbody.querySelectorAll('tr')
    if (rows[index]) {
      rows[index].remove()
      this.updateRowIndices(table)
    }
  }

  static addColumn(table, index) {
    const rows = table.querySelectorAll('tr')
    
    rows.forEach((row, rowIndex) => {
      const cell = document.createElement('td')
      cell.style.border = '1px solid #ccc'
      cell.style.padding = '8px'
      cell.style.minWidth = '50px'
      cell.style.minHeight = '30px'
      cell.style.position = 'relative'
      cell.dataset.colIndex = index
      cell.innerHTML = '<br>'
      
      const cells = row.querySelectorAll('td')
      if (index >= cells.length) {
        row.appendChild(cell)
      } else {
        row.insertBefore(cell, cells[index])
      }
    })
    
    // Update column indices
    this.updateColumnIndices(table)
    
    // Add event listeners to new cells
    this.addEventListenersToTable(table)
  }

  static deleteColumn(table, index) {
    const rows = table.querySelectorAll('tr')
    
    rows.forEach(row => {
      const cells = row.querySelectorAll('td')
      if (cells[index]) {
        cells[index].remove()
      }
    })
    
    // Update column indices
    this.updateColumnIndices(table)
  }

  static updateRowIndices(table) {
    const rows = table.querySelectorAll('tr')
    rows.forEach((row, index) => {
      row.dataset.rowIndex = index
    })
  }

  static updateColumnIndices(table) {
    const rows = table.querySelectorAll('tr')
    rows.forEach(row => {
      const cells = row.querySelectorAll('td')
      cells.forEach((cell, index) => {
        cell.dataset.colIndex = index
      })
    })
  }
}

// Register the simple table blot
Quill.register(SimpleTableBlot)

// Custom HTML parser to handle existing table content
const clipboardModule = Quill.import('modules/clipboard')
const originalClipboardMatchers = clipboardModule.matchers || []
const Delta = Quill.import('delta')

// Override clipboard matchers to handle table HTML
const customMatchers = [
  // Table matcher - must come before the default block matcher
  (node, delta) => {
    if (node.tagName === 'TABLE' && node.classList.contains('ql-table')) {
      // Create a custom table blot
      const tableBlot = SimpleTableBlot.createFromHTML(node.outerHTML)
      if (tableBlot) {
        return new Delta().insert({ table: SimpleTableBlot.value(tableBlot) })
      }
    }
    return null
  },
  // Include all original matchers (ensure it's an array)
  ...(Array.isArray(originalClipboardMatchers) ? originalClipboardMatchers : [])
]

// Register the custom matchers
clipboardModule.matchers = customMatchers

// Global map to store table cell content - using a simple array-based approach per table
const tableContentStore = new Map()
let currentProcessingId = null

// Custom function to process HTML content and convert tables
const processTableHTML = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') return htmlContent
  
  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContent
  
  // Find all tables and ensure they have the ql-table class
  const tables = tempDiv.querySelectorAll('table')
  tables.forEach((table, tableIndex) => {
    if (!table.classList.contains('ql-table')) {
      table.classList.add('ql-table')
    }
    
    // Generate a processing ID if not already set
    if (!currentProcessingId) {
      currentProcessingId = `processing-${Date.now()}`
      console.log(`🔧 Starting new processing session: ${currentProcessingId}`)
    }
    
    // Ensure proper styling
    table.setAttribute('border', '1')
    table.setAttribute('cellpadding', '0')
    table.setAttribute('cellspacing', '0')
    table.style.borderCollapse = 'collapse'
    table.style.width = '100%'
    table.style.position = 'relative'
    
    // Ensure all cells have proper content and styling
    const cells = table.querySelectorAll('td')
    const cellContents = []
    
    cells.forEach((cell, cellIndex) => {
      const cellContent = cell.innerHTML
      
      // Store cell content in our array
      if (cellContent && cellContent.trim() !== '' && cellContent !== '<br>') {
        cellContents.push(cellContent)
        console.log(`🔧 Storing cell ${cellIndex} content: "${cellContent}"`)
      } else {
        cellContents.push(null)
      }
      
      // Ensure cell has proper styling
      cell.style.border = '1px solid #ccc'
      cell.style.padding = '8px'
      cell.style.minWidth = '50px'
      cell.style.minHeight = '30px'
      cell.style.position = 'relative'
      
      // Ensure content is present
      if (!cellContent || cellContent.trim() === '') {
        cell.innerHTML = '<br>'
      }
    })
    
          // Store the cell contents array for this table (only if not already stored)
          const storeKey = `${currentProcessingId}-table-${tableIndex}`
          if (!tableContentStore.has(storeKey)) {
            tableContentStore.set(storeKey, cellContents)
            console.log(`🔧 Stored ${cellContents.length} cells for table ${tableIndex} under key: ${storeKey}`)
          } else {
            console.log(`🔧 Content already stored for table ${tableIndex} under key: ${storeKey}`)
          }
    
    // Ensure proper row indices
    const rows = table.querySelectorAll('tr')
    rows.forEach((row, index) => {
      row.dataset.rowIndex = index
      const cells = row.querySelectorAll('td')
      cells.forEach((cell, colIndex) => {
        cell.dataset.colIndex = colIndex
      })
    })
    
    // Ensure there's a paragraph after each table for text input
    const nextSibling = table.nextSibling
    if (!nextSibling || nextSibling.nodeType !== Node.ELEMENT_NODE || nextSibling.tagName !== 'P') {
      const paragraph = document.createElement('p')
      paragraph.innerHTML = '<br>'
      table.parentNode.insertBefore(paragraph, table.nextSibling)
      console.log(`🔧 Added paragraph after table ${tableIndex} for text input`)
    }
  })
  
  return tempDiv.innerHTML
}

// Function to ensure table content stays within table cells
const fixTableContent = (quill) => {
  if (!quill || !quill.root) return
  
  const tables = quill.root.querySelectorAll('table.ql-table')
  tables.forEach(table => {
    // Only fix tables that actually have issues
    const cells = table.querySelectorAll('td, th')
    const rows = table.querySelectorAll('tr')
    
    // If table has no cells or rows, skip it
    if (cells.length === 0 || rows.length === 0) {
      console.log('🔧 Skipping table with no cells or rows')
      return
    }
    
    // Check if table structure is actually broken
    let needsFix = false
    
    // Check for content outside of cells (direct text nodes or elements)
    const walker = document.createTreeWalker(
      table,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          // Skip if it's a cell or inside a cell
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'TD' || node.tagName === 'TH' || 
                node.closest('td, th')) {
              return NodeFilter.FILTER_REJECT
            }
            // If it's a direct child of table but not tbody/thead/tfoot, it's a problem
            if (node.parentNode === table && 
                !['TBODY', 'THEAD', 'TFOOT'].includes(node.tagName)) {
              needsFix = true
              return NodeFilter.FILTER_ACCEPT
            }
          }
          // Text nodes that are direct children of table are problematic
          if (node.nodeType === Node.TEXT_NODE && 
              node.parentNode === table && 
              node.textContent.trim()) {
            needsFix = true
            return NodeFilter.FILTER_ACCEPT
          }
          return NodeFilter.FILTER_REJECT
        }
      }
    )
    
    // Walk through the tree to check for issues
    while (walker.nextNode()) {
      // If we found problematic content, mark for fix
      needsFix = true
      break
    }
    
    // Only fix if there are actual structural issues
    if (!needsFix) {
      console.log('🔧 Table structure is fine, no fix needed')
      return
    }
    
    console.log('🔧 Table needs fixing, reconstructing...')
    
    // Extract all cells and their content
    const cellData = []
    cells.forEach(cell => {
      cellData.push({
        content: cell.innerHTML,
        rowIndex: parseInt(cell.parentElement.dataset.rowIndex) || 0,
        colIndex: parseInt(cell.dataset.colIndex) || 0
      })
    })
    
    // Group cells by rows
    const rowsData = {}
    cellData.forEach(cell => {
      if (!rowsData[cell.rowIndex]) {
        rowsData[cell.rowIndex] = []
      }
      rowsData[cell.rowIndex].push(cell)
    })
    
    // Create new table
    const newTable = document.createElement('table')
    newTable.className = 'ql-table'
    newTable.setAttribute('border', '1')
    newTable.setAttribute('cellpadding', '0')
    newTable.setAttribute('cellspacing', '0')
    newTable.style.borderCollapse = 'collapse'
    newTable.style.width = '100%'
    newTable.style.position = 'relative'
    
    const tbody = document.createElement('tbody')
    
    // Create rows
    Object.keys(rowsData).sort((a, b) => parseInt(a) - parseInt(b)).forEach(rowIndex => {
      const row = document.createElement('tr')
      row.dataset.rowIndex = rowIndex
      
      rowsData[rowIndex].sort((a, b) => a.colIndex - b.colIndex).forEach(cell => {
        const newCell = document.createElement('td')
        newCell.innerHTML = cell.content || '<br>'
        newCell.dataset.colIndex = cell.colIndex
        newCell.style.border = '1px solid #ccc'
        newCell.style.padding = '8px'
        newCell.style.minWidth = '50px'
        newCell.style.minHeight = '30px'
        newCell.style.position = 'relative'
        
        row.appendChild(newCell)
      })
      
      tbody.appendChild(row)
    })
    
    newTable.appendChild(tbody)
    
    // Ensure there's a paragraph after the table for text input
    const nextSibling = table.nextSibling
    if (!nextSibling || nextSibling.nodeType !== Node.ELEMENT_NODE || nextSibling.tagName !== 'P') {
      const paragraph = document.createElement('p')
      paragraph.innerHTML = '<br>'
      table.parentNode.insertBefore(paragraph, table.nextSibling)
      console.log('🔧 Added paragraph after table for text input')
    }
    
    // Replace the old table with the new one
    table.parentNode.replaceChild(newTable, table)
    
    console.log('🔧 Table reconstructed successfully')
  })
}


// Function to add event listeners to tables after Quill processes content
const addTableEventListeners = (quill) => {
  if (!quill || !quill.root) return
  
  const tables = quill.root.querySelectorAll('table.ql-table')
  tables.forEach(table => {
    const cells = table.querySelectorAll('td')
    cells.forEach(cell => {
      // Skip if already has event listeners
      if (cell.dataset.hasListeners === 'true') return
      
      // Add context menu functionality for desktop and mobile
      cell.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        SimpleTableBlot.showContextMenu(e, cell, table)
      })
      
      // Add mobile touch support
      let touchStartTime = 0
      let touchTimer = null
      
      cell.addEventListener('touchstart', (e) => {
        touchStartTime = Date.now()
        touchTimer = setTimeout(() => {
          // Long press detected
          e.preventDefault()
          SimpleTableBlot.showContextMenu(e, cell, table)
        }, 500) // 500ms long press
      })
      
      cell.addEventListener('touchend', (e) => {
        if (touchTimer) {
          clearTimeout(touchTimer)
          touchTimer = null
        }
      })
      
      cell.addEventListener('touchmove', (e) => {
        if (touchTimer) {
          clearTimeout(touchTimer)
          touchTimer = null
        }
      })
      
      // Add visual feedback for mobile
      cell.addEventListener('touchstart', (e) => {
        cell.style.backgroundColor = '#f0f0f0'
      })
      
      cell.addEventListener('touchend', (e) => {
        setTimeout(() => {
          cell.style.backgroundColor = ''
        }, 100)
      })
      
      // Mark as having listeners
      cell.dataset.hasListeners = 'true'
    })
    
    // Add table-level event listeners
    table.addEventListener('click', (e) => {
      if (e.target.tagName === 'TD') {
        SimpleTableBlot.hideContextMenu()
      }
    })
  })
}


/**
 * Rich Text Editor Component
 * 
 * A reusable rich text editor component using React Quill with emoji support.
 * Supports formatting options like bold, italic, lists, links, etc.
 * Includes automatic image compression for optimal database storage.
 */
const RichTextEditor = forwardRef(({ 
  value, 
  onChange, 
  placeholder = "Start typing your note...",
  className = "",
  disabled = false,
  error = false,
  isFullscreen = false,
  maxLength = null,
  showCharacterCount = false,
  noteTitle = "",
  currentNoteId = null, // For note linking - exclude current note from selection
  currentFolderId = null // For note linking - detect sensitive context
}, ref) => {
  const quillRef = useRef(null)
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false)
  const [isAIModalOpen, setIsAIModalOpen] = useState(false)
  const [aiQuery, setAiQuery] = useState('')
  const [aiResponse, setAiResponse] = useState('')
  const [isLoadingAI, setIsLoadingAI] = useState(false)
  const [showAIResponse, setShowAIResponse] = useState(false)
  const [savedLineNumber, setSavedLineNumber] = useState(null)
  const [savedLinePosition, setSavedLinePosition] = useState(null)
  const [isDrawingModalOpen, setIsDrawingModalOpen] = useState(false)
  
  // Note linking state
  const [isNoteLinkModalOpen, setIsNoteLinkModalOpen] = useState(false)
  const [mentionCursorIndex, setMentionCursorIndex] = useState(null)
  const [mentionPartialText, setMentionPartialText] = useState('')
  
  // Contexts for note linking
  const { folders } = useFolders()
  const { openTab } = useTabs()
  
  // Check if current context is sensitive (disable @ mentions in sensitive notes)
  const isSensitiveContext = useMemo(() => {
    if (currentFolderId) {
      const folder = folders.find(f => f.folderId === currentFolderId)
      return folder?.folderType === 'SENSITIVE'
    }
    return false
  }, [currentFolderId, folders])
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false)
  const [editingImage, setEditingImage] = useState(null)
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false)
  const [colorPickerType, setColorPickerType] = useState('text')
  const [isPickerModalOpen, setIsPickerModalOpen] = useState(false)
  const [pickerType, setPickerType] = useState('header')
  const isInsertingAttachment = useRef(false)
  
  const isPastingImage = useRef(false)
  const lastSetValue = useRef(null)
  const { isLocalMode, uploadFileLocal, getLocalFile } = useLocalMode()
  
  // Image resize functionality state
  const [selectedImage, setSelectedImage] = useState(null)
  const [resizeHandlesVisible, setResizeHandlesVisible] = useState(false)
  const [isImageResizeModalOpen, setIsImageResizeModalOpen] = useState(false)
  const [resizeImageData, setResizeImageData] = useState(null)
  
  // Switch to modal-based resize to avoid Quill conflicts
  const ENABLE_IMAGE_RESIZE = true
  const USE_MODAL_RESIZE = true
  
  /**
   * Handle emoji selection and insertion
   */
  const handleEmojiSelect = (emoji) => {
    const quill = quillRef.current?.getEditor()
    if (quill) {
      const range = quill.getSelection(true)
      quill.insertText(range.index, emoji, 'user')
      quill.setSelection(range.index + emoji.length)
    }
  }

  /**
   * Open color picker modal
   */
  const handleOpenColorPicker = (type) => {
    setColorPickerType(type)
    setIsColorPickerOpen(true)
  }

  /**
   * Handle color selection from modal
   */
  const handleColorSelect = (color) => {
    const quill = quillRef.current?.getEditor()
    if (quill) {
      const range = quill.getSelection(true)
      if (range) {
        if (colorPickerType === 'text') {
          quill.formatText(range.index, range.length, 'color', color)
        } else {
          quill.formatText(range.index, range.length, 'background', color)
        }
        quill.setSelection(range.index + range.length)
      }
    }
  }

  /**
   * Close color picker modal
   */
  const handleCloseColorPicker = () => {
    setIsColorPickerOpen(false)
    setColorPickerType('text')
  }

  /**
   * Open picker modal
   */
  const handleOpenPicker = (type) => {
    setPickerType(type)
    setIsPickerModalOpen(true)
  }

  /**
   * Handle picker selection from modal
   */
  const handlePickerSelect = (value) => {
    console.log('🔍 Header picker select called with value:', value, 'type:', pickerType)
    const quill = quillRef.current?.getEditor()
    if (quill) {
      const range = quill.getSelection(true)
      console.log('🔍 Current selection range:', range)
      if (range) {
        // If no text is selected, select the current line
        let formatRange = range
        if (range.length === 0) {
          const [line] = quill.getLine(range.index)
          if (line) {
            const lineStart = quill.getIndex(line)
            const lineLength = line.length()
            formatRange = { index: lineStart, length: lineLength }
            console.log('🔍 No selection, using line range:', formatRange)
          }
        }
        switch (pickerType) {
          case 'header':
            console.log('🔍 Applying header format:', value)
            
            // Debug: Check what formats are available
            const availableFormats = quill.getModule('toolbar').container.querySelectorAll('button')
            console.log('🔍 Available toolbar buttons:', Array.from(availableFormats).map(b => b.className))
            
            // Check if Quill recognizes the header format
            const formats = quill.getFormat()
            console.log('🔍 Current formats available:', Object.keys(formats))
            
            // Debug: Check what modules are loaded
            console.log('🔍 Quill modules:', Object.keys(quill.getModule ? quill.getModule() || {} : {}))
            
            // Debug: Check if header is in the formats array
            console.log('🔍 Configured formats:', quill.options?.formats || [])
            
            // Convert false to null for removing header formatting
            const headerValue = value === false ? null : value
            console.log('🔍 Converted header value:', headerValue)
            
            // Use Quill's native header formatting (which we know works now)
            quill.format('header', headerValue)
            
            // Debug: Check what was actually applied
            setTimeout(() => {
              const currentFormat = quill.getFormat(formatRange.index, formatRange.length)
              console.log('🔍 Format after applying header:', currentFormat)
              
              // Also check the HTML content
              const content = quill.root.innerHTML
              console.log('🔍 Editor HTML after header format:', content.substring(0, 200) + '...')
            }, 100)
            break
          case 'align':
            quill.format('align', value)
            break
          default:
            break
        }
        quill.setSelection(formatRange.index + formatRange.length)
      }
    }
  }

  /**
   * Close picker modal
   */
  const handleClosePicker = () => {
    setIsPickerModalOpen(false)
    setPickerType('header')
  }

  /**
   * Update old attachment links with hash URLs or about:blank to use javascript:void(0)
   */
  const updateOldAttachmentLinks = () => {
    const editor = quillRef.current?.getEditor()
    if (!editor) return

    const editorElement = editor.container.querySelector('.ql-editor')
    if (!editorElement) return

    // Look for links with hash URLs, about:blank, or any link that looks like an attachment
    const oldLinks = editorElement.querySelectorAll('a[href^="#local-attachment-"], a[href^="#cloud-attachment-"], a[href="about:blank"], a[data-file-id]')
    console.log(`Found ${oldLinks.length} potential attachment links to update`)
    
    oldLinks.forEach((link, index) => {
      const fileId = link.getAttribute('data-file-id')
      const fileName = link.getAttribute('data-file-name') || link.textContent
      const currentHref = link.getAttribute('href')
      
      console.log(`🔍 Processing link ${index}:`, {
        fileId,
        fileName,
        currentHref,
        textContent: link.textContent,
        allAttributes: Array.from(link.attributes).map(attr => `${attr.name}="${attr.value}"`).join(', ')
      })
      
      // Check if this looks like an attachment link
      const isAttachmentLink = fileId || 
                              currentHref?.includes('attachment') || 
                              currentHref === 'about:blank' ||
                              link.getAttribute('data-download-url')
      
      if (isAttachmentLink && fileName) {
        // Update the link to use javascript:void(0) and proper styling
        link.href = 'javascript:void(0)'
        link.removeAttribute('target')
        link.removeAttribute('rel')
        link.style.color = '#3b82f6'
        link.style.textDecoration = 'underline'
        link.style.cursor = 'pointer'
        
        // For existing links, try to extract the real file ID from the content or use a different approach
        if (!fileId && link.textContent) {
          // Don't generate a temp ID - instead, mark it as needing special handling
          link.setAttribute('data-file-name', fileName)
          link.setAttribute('data-needs-special-handling', 'true')
          console.log(`Marked attachment link for special handling: ${fileName}`)
        }
        
        console.log(`Updated attachment link: ${fileName} (href was: ${currentHref})`)
      }
    })
  }

  /**
   * Process existing attachment links and add download handlers
   */
  const processExistingAttachmentLinks = () => {
    const editor = quillRef.current?.getEditor()
    if (!editor) {
      console.log('No editor found in processExistingAttachmentLinks')
      return
    }

    const editorElement = editor.container.querySelector('.ql-editor')
    if (!editorElement) {
      console.log('No editor element found')
      return
    }

    const attachmentLinks = editorElement.querySelectorAll('a[data-file-id], a[href="javascript:void(0)"]')
    console.log(`Found ${attachmentLinks.length} attachment links to process`)

    attachmentLinks.forEach((link, index) => {
      const fileId = link.getAttribute('data-file-id')
      const fileName = link.getAttribute('data-file-name') || link.textContent
      
      if (!fileName) {
        console.log(`Skipping link ${index}: missing fileName (${fileName})`)
        return
      }
      
      // If no fileId, we'll handle it in the global click handler
      if (!fileId) {
        console.log(`Link ${index} has no fileId, will be handled by global click handler: ${fileName}`)
        console.log('🎵 Link attributes:', {
          href: link.getAttribute('href'),
          dataFileId: link.getAttribute('data-file-id'),
          dataFileName: link.getAttribute('data-file-name'),
          dataCloudFile: link.getAttribute('data-cloud-file'),
          dataLocalFile: link.getAttribute('data-local-file'),
          dataDownloadUrl: link.getAttribute('data-download-url')
        })
        return
      }

      // Remove any existing click handlers to prevent duplicates
      const newLink = link.cloneNode(true)
      link.parentNode.replaceChild(newLink, link)
      
      newLink.addEventListener('click', async (e) => {
        e.preventDefault()
        e.stopPropagation()
        
        console.log(`🖱️ Attachment link clicked: ${fileName} (${fileId})`)
        console.log(`🖱️ Event prevented: ${e.defaultPrevented}`)
        
        try {
          // Check if this is a local file
          const isLocalFile = newLink.getAttribute('data-local-file') === 'true' || 
                             newLink.getAttribute('data-download-url')?.startsWith('local://')
          
          if (isLocalFile && isLocalMode) {
            // Handle local file download
            console.log('Downloading local file:', fileId)
            
            if (!getLocalFile) {
              throw new Error('Local file operations not available')
            }
            
            // Get the local file from LocalModeContext (need to import getLocalFile)
            const localFile = await getLocalFile(fileId)
            
            const url = window.URL.createObjectURL(localFile.blob)
            const a = document.createElement('a')
            a.href = url
            a.download = localFile.fileName
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            setTimeout(() => {
              window.URL.revokeObjectURL(url)
              document.body.removeChild(a)
            }, 100)
            
          } else {
            // Handle cloud file download (existing logic)
          const response = await fetch(`/api/files/${fileId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
          
          if (response.ok) {
            console.log('Download response successful')
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            setTimeout(() => {
              window.URL.revokeObjectURL(url)
              document.body.removeChild(a)
            }, 100)
          } else {
            console.log('Download response failed:', response.status)
            toast.error('Failed to download file')
            }
          }
        } catch (error) {
          console.error('Download failed:', error)
          toast.error('Failed to download file')
        }
        
        return false
      })
      
      console.log(`Click handler attached to link ${index}:`, fileName)
    })
  }

  // Helper function to convert blob to data URL
  const blobToDataUrl = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  // Helper function to process content for saving - replace base64 images with Google Drive URLs
  const processContentForSave = (content) => {
    if (!content) return content
    
    console.log('🔍 processContentForSave: Input content:', content.substring(0, 200) + '...')
    
    // Get the actual DOM elements from the Quill editor instead of parsing HTML string
    const quill = quillRef.current?.getEditor()
    if (!quill) {
      console.warn('⚠️ processContentForSave: Quill editor not available')
      return content
    }
    
    // Find all img elements with data-google-drive-url attribute in the actual DOM
    const imgElements = quill.root.querySelectorAll('img[data-google-drive-url]')
    console.log('🔍 processContentForSave: Found', imgElements.length, 'images with data-google-drive-url attribute')
    
    // Also check for any img elements without the attribute
    const allImgElements = quill.root.querySelectorAll('img')
    console.log('🔍 processContentForSave: Total img elements:', allImgElements.length)
    
    allImgElements.forEach((img, index) => {
      console.log(`🔍 processContentForSave: Image ${index}:`, {
        src: img.src.substring(0, 50) + '...',
        hasDataAttribute: img.hasAttribute('data-google-drive-url'),
        dataAttribute: img.getAttribute('data-google-drive-url')
      })
    })
    
    // Process each image with data attribute
    imgElements.forEach((img, index) => {
      const googleDriveUrl = img.getAttribute('data-google-drive-url')
      if (googleDriveUrl) {
        // Preserve width and height attributes before replacing src
        const width = img.getAttribute('width')
        const height = img.getAttribute('height')
        const styleWidth = img.style.width
        const styleHeight = img.style.height
        
        // Replace the src with the Google Drive URL
        img.src = googleDriveUrl
        
        // Restore size attributes after src change
        if (width) img.setAttribute('width', width)
        if (height) img.setAttribute('height', height)
        if (styleWidth) img.style.width = styleWidth
        if (styleHeight) img.style.height = styleHeight
        
        // Remove the data attribute
        img.removeAttribute('data-google-drive-url')
        
        console.log(`🔄 processContentForSave: Image ${index + 1} - Replaced base64 with Google Drive URL:`, {
          googleDriveUrl: googleDriveUrl,
          preservedWidth: width,
          preservedHeight: height,
          preservedStyleWidth: styleWidth,
          preservedStyleHeight: styleHeight
        })
      }
    })
    
    // Get the updated content from the DOM
    const result = quill.root.innerHTML
    console.log('🔍 processContentForSave: Output content:', result.substring(0, 200) + '...')
    return result
  }

  /**
   * Handle image upload with file-based storage
   */
  const handleImageUpload = async (file) => {
    console.log('🔍 handleImageUpload called with file:', file.name, file.type, file.size)
    if (!file) return

    let spinnerToast = null
    try {
      // Show floating spinner with progress updates
      spinnerToast = toast.loading(
        `Uploading ${file.name}...`, 
        {
          duration: Infinity, // Don't auto-dismiss
          style: {
            background: 'rgba(31, 41, 55, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }
      )
      // Update spinner for processing
      toast.loading(`Processing ${file.name}...`, {
        id: spinnerToast,
        duration: Infinity,
        style: {
          background: 'rgba(31, 41, 55, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      })
      
      // Upload image using the new file-based service
      const uploadResult = await uploadImageFile(file, isLocalMode)
      
      console.log('🔍 handleImageUpload: Upload result:', uploadResult)
      
      if (!uploadResult.success) {
        toast.dismiss(spinnerToast)
        toast.error(`Image upload failed: ${uploadResult.error}`, {
          duration: 5000,
          style: {
            background: 'rgba(239, 68, 68, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }
        })
      return
    }

      console.log('🔍 handleImageUpload: Upload successful, proceeding to editor insertion')
      
      // Update spinner for getting display URL
      toast.loading(`Preparing ${file.name}...`, {
        id: spinnerToast,
        duration: Infinity,
        style: {
          background: 'rgba(31, 41, 55, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      })
      
      // Insert image into editor using the image reference
      // Wait for Quill editor to be ready with retry mechanism
      let quill = quillRef.current?.getEditor()
      let retryCount = 0
      const maxRetries = 20 // Increased from 10 to 20
      const retryDelay = 200 // Increased from 100ms to 200ms
      
      console.log('🔍 handleImageUpload: Initial Quill editor check:', { 
        quillExists: !!quill, 
        isLocalMode,
        quillRefExists: !!quillRef.current,
        quillRefType: typeof quillRef.current
      })
      
      // Retry mechanism to wait for Quill editor
      while (!quill && retryCount < maxRetries) {
        console.log(`🔍 handleImageUpload: Quill editor not ready, retry ${retryCount + 1}/${maxRetries}`)
        await new Promise(resolve => setTimeout(resolve, retryDelay))
        quill = quillRef.current?.getEditor()
        retryCount++
        
        // Additional debugging every 5 retries
        if (retryCount % 5 === 0) {
          console.log(`🔍 handleImageUpload: Retry ${retryCount} - quillRef.current:`, {
            exists: !!quillRef.current,
            hasGetEditor: !!(quillRef.current?.getEditor),
            type: typeof quillRef.current
          })
        }
      }
      
      console.log('🔍 handleImageUpload: Final Quill editor check:', { 
        quillExists: !!quill, 
        isLocalMode, 
        retryCount,
        totalWaitTime: retryCount * retryDelay
      })
      
      if (quill) {
        const range = quill.getSelection() || { index: quill.getLength() }
        
        if (isLocalMode) {
          // Local mode - use data URL directly
          const imageData = await localStorageService.getImageAttachment(uploadResult.attachmentId)
          const dataUrl = await blobToDataUrl(imageData.blob)
          
          const imageHtml = `<img src="${dataUrl}" alt="${uploadResult.fileName || 'image'}" style="max-width: 100%; height: auto;" />`
          quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml, 'user')
        quill.setSelection(range.index + 1)
          
          console.log('🖼️ Image inserted into editor (local mode):', {
            fileName: uploadResult.fileName,
            range: range.index
          })
        } else {
          // Cloud mode - use base64 from cache for display, but store Google Drive URL in content
          console.log('🔍 handleImageUpload: Cloud mode, checking googleDriveUrl:', uploadResult.googleDriveUrl)
          console.log('🔍 handleImageUpload: Entering cloud mode logic')
          if (uploadResult.googleDriveUrl) {
            // Get the base64 image from cache for display
            const fileId = extractFileIdFromUrl(uploadResult.googleDriveUrl)
            console.log('🔍 RichTextEditor: Getting base64 for fileId:', fileId)
            const base64DataUrl = await getImageBase64(uploadResult.googleDriveUrl, fileId)
            console.log('🔍 RichTextEditor: Got base64DataUrl:', base64DataUrl ? 'YES' : 'NO', base64DataUrl ? base64DataUrl.substring(0, 50) + '...' : 'null')
            
            if (base64DataUrl) {
              // Display base64 image in editor using Quill's insertEmbed method
              quill.insertEmbed(range.index, 'image', base64DataUrl, 'user')
              quill.setSelection(range.index + 1)
              
              // Store the Google Drive URL in a data attribute immediately for later use during save
              // This way the image displays as base64 but we can replace it when saving
              const imgElements = quill.root.querySelectorAll('img')
              console.log('🔍 Setting data attribute: Found', imgElements.length, 'img elements')
              const lastImg = imgElements[imgElements.length - 1]
              console.log('🔍 Setting data attribute: Last img element:', {
                exists: !!lastImg,
                src: lastImg ? lastImg.src.substring(0, 50) + '...' : 'none',
                expectedSrc: base64DataUrl.substring(0, 50) + '...',
                srcMatch: lastImg ? lastImg.src === base64DataUrl : false
              })
              if (lastImg && lastImg.src === base64DataUrl) {
                lastImg.setAttribute('data-google-drive-url', uploadResult.googleDriveUrl)
                console.log('🔗 Stored Google Drive URL in data attribute for save-time replacement:', uploadResult.googleDriveUrl)
              } else {
                console.warn('⚠️ Could not find matching image element to set data attribute')
              }
              
              console.log('🖼️ Image inserted into editor (cloud mode):', {
                googleDriveUrl: uploadResult.googleDriveUrl,
                base64DataUrl: base64DataUrl.substring(0, 50) + '...',
                fileName: uploadResult.fileName,
                range: range.index
              })
            } else {
              // Fallback: use Google Drive URL directly if base64 not available
              const imageHtml = `<img src="${uploadResult.googleDriveUrl}" alt="${uploadResult.fileName || 'image'}" style="max-width: 100%; height: auto;" />`
              quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml, 'user')
              quill.setSelection(range.index + 1)
              
              console.log('🖼️ Image inserted into editor (cloud mode - fallback to URL):', {
                googleDriveUrl: uploadResult.googleDriveUrl,
                fileName: uploadResult.fileName,
                range: range.index
              })
            }
          } else {
            // Fallback to local mode if no Google Drive URL
            const imageData = await localStorageService.getImageAttachment(uploadResult.localId)
            const dataUrl = await blobToDataUrl(imageData.blob)
            
            const imageHtml = `<img src="${dataUrl}" alt="${uploadResult.fileName || 'image'}" style="max-width: 100%; height: auto;" />`
            quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml, 'user')
            quill.setSelection(range.index + 1)
            
            console.log('🖼️ Image inserted into editor (fallback to local):', {
              fileName: uploadResult.fileName,
              range: range.index
            })
          }
        }
      } else {
        console.error(`❌ RichTextEditor: Quill editor not available after ${retryCount} retries (${retryCount * retryDelay}ms)`)
        toast.dismiss(spinnerToast)
        toast.error(`Editor not ready after ${retryCount} attempts. Please try again.`, {
          duration: 5000,
          style: {
            background: 'rgba(239, 68, 68, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }
        })
        return
      }
      
      // Dismiss spinner and show success
      toast.dismiss(spinnerToast)
      
      // Show compression info with glassmorphic styling
      if (uploadResult.wasCompressed) {
        toast.success(
          `Image compressed: ${uploadResult.originalSize.toFixed(1)}KB → ${uploadResult.compressedSize.toFixed(1)}KB`,
          { 
            duration: 3000,
            style: {
              background: 'rgba(16, 185, 129, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }
          }
        )
      } else {
        toast.success('Image added successfully!', {
          duration: 3000,
          style: {
            background: 'rgba(16, 185, 129, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }
        })
      }
      
    } catch (error) {
      // Dismiss spinner and show error
      if (spinnerToast) {
        toast.dismiss(spinnerToast)
      }
      console.error('❌ Image upload failed:', error)
      toast.error(`Failed to process image: ${error.message}`, {
        duration: 5000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
    }
  }

  /**
   * Handle pasted images with file-based storage
   */
  const handlePastedImage = async (file) => {
    if (isPastingImage.current) return // Prevent multiple simultaneous pastes
    isPastingImage.current = true

    // Show floating spinner for pasted images
    const spinnerToast = toast.loading(
      `Processing pasted image...`, 
      {
        duration: Infinity, // Don't auto-dismiss
        style: {
          background: 'rgba(31, 41, 55, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      }
    )

    try {
      // Update spinner for processing
      toast.loading(`Compressing pasted image...`, {
        id: spinnerToast,
        duration: Infinity,
        style: {
          background: 'rgba(31, 41, 55, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      })
      
      // Upload pasted image using the new file-based service
      const uploadResult = await uploadImageFile(file, isLocalMode)
      
      if (!uploadResult.success) {
        toast.dismiss(spinnerToast)
        toast.error(`Image upload failed: ${uploadResult.error}`, {
          duration: 5000,
          style: {
            background: 'rgba(239, 68, 68, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }
        })
        return
      }
      
      // Update spinner for getting display URL
      toast.loading(`Preparing pasted image...`, {
        id: spinnerToast,
        duration: Infinity,
        style: {
          background: 'rgba(31, 41, 55, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      })
      
      // Insert image into editor using the image reference
      const quill = quillRef.current?.getEditor()
      if (quill) {
        const range = quill.getSelection() || { index: quill.getLength() }
        
        if (isLocalMode) {
          // Local mode - use data URL directly
          const imageData = await localStorageService.getImageAttachment(uploadResult.attachmentId)
          const dataUrl = await blobToDataUrl(imageData.blob)
          
          const imageHtml = `<img src="${dataUrl}" alt="${uploadResult.fileName || 'image'}" style="max-width: 100%; height: auto;" />`
          quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml, 'user')
        quill.setSelection(range.index + 1)
          
          console.log('🖼️ Image inserted into editor (local mode):', {
            fileName: uploadResult.fileName,
            range: range.index
          })
        } else {
          // Cloud mode - use base64 from cache for display, but store Google Drive URL in content
          if (uploadResult.googleDriveUrl) {
            // Get the base64 image from cache for display
            const fileId = extractFileIdFromUrl(uploadResult.googleDriveUrl)
            console.log('🔍 RichTextEditor: Getting base64 for fileId:', fileId)
            const base64DataUrl = await getImageBase64(uploadResult.googleDriveUrl, fileId)
            console.log('🔍 RichTextEditor: Got base64DataUrl:', base64DataUrl ? 'YES' : 'NO', base64DataUrl ? base64DataUrl.substring(0, 50) + '...' : 'null')
            
            if (base64DataUrl) {
              // Display base64 image in editor using Quill's insertEmbed method
              quill.insertEmbed(range.index, 'image', base64DataUrl, 'user')
              quill.setSelection(range.index + 1)
              
              // Store the Google Drive URL in a data attribute immediately for later use during save
              // This way the image displays as base64 but we can replace it when saving
              const imgElements = quill.root.querySelectorAll('img')
              console.log('🔍 Setting data attribute: Found', imgElements.length, 'img elements')
              const lastImg = imgElements[imgElements.length - 1]
              console.log('🔍 Setting data attribute: Last img element:', {
                exists: !!lastImg,
                src: lastImg ? lastImg.src.substring(0, 50) + '...' : 'none',
                expectedSrc: base64DataUrl.substring(0, 50) + '...',
                srcMatch: lastImg ? lastImg.src === base64DataUrl : false
              })
              if (lastImg && lastImg.src === base64DataUrl) {
                lastImg.setAttribute('data-google-drive-url', uploadResult.googleDriveUrl)
                console.log('🔗 Stored Google Drive URL in data attribute for save-time replacement:', uploadResult.googleDriveUrl)
              } else {
                console.warn('⚠️ Could not find matching image element to set data attribute')
              }
              
              console.log('🖼️ Image inserted into editor (cloud mode):', {
                googleDriveUrl: uploadResult.googleDriveUrl,
                base64DataUrl: base64DataUrl.substring(0, 50) + '...',
                fileName: uploadResult.fileName,
                range: range.index
              })
            } else {
              // Fallback: use Google Drive URL directly if base64 not available
              const imageHtml = `<img src="${uploadResult.googleDriveUrl}" alt="${uploadResult.fileName || 'image'}" style="max-width: 100%; height: auto;" />`
              quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml, 'user')
              quill.setSelection(range.index + 1)
              
              console.log('🖼️ Image inserted into editor (cloud mode - fallback to URL):', {
                googleDriveUrl: uploadResult.googleDriveUrl,
                fileName: uploadResult.fileName,
                range: range.index
              })
            }
          } else {
            // Fallback to local mode if no Google Drive URL
            const imageData = await localStorageService.getImageAttachment(uploadResult.localId)
            const dataUrl = await blobToDataUrl(imageData.blob)
            
            const imageHtml = `<img src="${dataUrl}" alt="${uploadResult.fileName || 'image'}" style="max-width: 100%; height: auto;" />`
            quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml, 'user')
            quill.setSelection(range.index + 1)
            
            console.log('🖼️ Image inserted into editor (fallback to local):', {
              fileName: uploadResult.fileName,
              range: range.index
            })
          }
        }
      }
      
      // Dismiss spinner and show success
      toast.dismiss(spinnerToast)
      
      // Show compression info with glassmorphic styling
      if (uploadResult.wasCompressed) {
        toast.success(
          `Pasted image compressed: ${uploadResult.originalSize.toFixed(1)}KB → ${uploadResult.compressedSize.toFixed(1)}KB`,
          { 
            duration: 3000,
            style: {
              background: 'rgba(16, 185, 129, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }
          }
        )
      } else {
        toast.success('Image pasted successfully!', {
          duration: 3000,
          style: {
            background: 'rgba(16, 185, 129, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }
        })
      }
      
    } catch (error) {
      // Dismiss spinner and show error
      toast.dismiss(spinnerToast)
      console.error('Pasted image upload failed:', error)
      toast.error(`Failed to process pasted image: ${error.message}`, {
        duration: 5000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
    } finally {
      isPastingImage.current = false
    }
  }

  /**
   * Handle file attachment
   */
  const handleFileAttachment = async (file) => {
    if (!file) return

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB.')
      return
    }

    try {
      // Show floating spinner with progress
      const spinnerToast = toast.loading(
        `Uploading ${file.name}...`, 
        {
          duration: Infinity, // Don't auto-dismiss
          style: {
            background: 'rgba(31, 41, 55, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }
      )
      
      // Upload file - use local upload in local mode, Google Drive direct upload in cloud mode
      let uploadResult
      if (isLocalMode) {
        // Update spinner for local mode
        toast.loading(`Saving ${file.name} locally...`, {
          id: spinnerToast,
          duration: Infinity,
          style: {
            background: 'rgba(31, 41, 55, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        })
        uploadResult = await uploadFileLocal(file)
      } else {
        // Update spinner for cloud mode
        toast.loading(`Uploading ${file.name} to cloud...`, {
          id: spinnerToast,
          duration: Infinity,
          style: {
            background: 'rgba(31, 41, 55, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        })
        
        // Upload directly to Google Drive (no backend dependency)
        if (!window.googleDriveAPI || !window.googleDriveAPI.accessToken) {
          throw new Error('Google Drive API not available or not authenticated. Please reconnect to Google Drive.')
        }
        uploadResult = await window.googleDriveAPI.uploadAttachment(file)
      }
      
      // Update spinner for processing
      toast.loading(`Processing ${file.name}...`, {
        id: spinnerToast,
        duration: Infinity,
        style: {
          background: 'rgba(31, 41, 55, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      })
      
      // Insert file as hyperlink
      const quill = quillRef.current?.getEditor()
      if (quill && uploadResult) {
        // Set flag to prevent content change processing during attachment insertion
        isInsertingAttachment.current = true
        
        const range = quill.getSelection() || { index: quill.getLength() }
        
        // Create attachment link HTML - different URLs for local vs cloud mode
        let attachmentHtml
        if (isLocalMode) {
          attachmentHtml = `<a href="javascript:void(0)" title="Download ${uploadResult.fileName}" data-file-id="${uploadResult.id}" data-file-name="${uploadResult.fileName}" data-download-url="local://${uploadResult.id}" data-handler-attached="true" data-local-file="true" style="color: #3b82f6; text-decoration: underline; cursor: pointer;">${uploadResult.fileName}</a>`
        } else {
          // Use Google Drive file ID for cloud mode (no backend dependency)
          const fileId = uploadResult.cloudFileId || uploadResult.id
          // Store the actual Google Drive filename for searching, but display the original filename
          const actualFileName = uploadResult.actualFileName || uploadResult.fileName
          attachmentHtml = `<a href="javascript:void(0)" title="Download ${uploadResult.fileName}" data-file-id="${fileId}" data-file-name="${actualFileName}" data-original-file-name="${uploadResult.fileName}" data-download-url="gdrive://${fileId}" data-handler-attached="true" data-cloud-file="true" style="color: #3b82f6; text-decoration: underline; cursor: pointer;">${uploadResult.fileName}</a>`
        }
        
        // Insert the link
        quill.clipboard.dangerouslyPasteHTML(range.index, attachmentHtml)
        quill.setSelection(range.index + uploadResult.fileName.length + 1)
        
        // Reset flag after a brief delay
        setTimeout(() => {
          isInsertingAttachment.current = false
        }, 100)
        
        // Process the newly added link
        setTimeout(() => {
          processExistingAttachmentLinks()
        }, 200)
      }
      
      // Dismiss spinner and show success
      toast.dismiss(spinnerToast)
      toast.success(`File "${file.name}" attached successfully!`, {
        duration: 3000,
        style: {
          background: 'rgba(16, 185, 129, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }
      })
      
    } catch (error) {
      // Dismiss spinner and show error
      toast.dismiss(spinnerToast)
      console.error('File attachment failed:', error)
      toast.error(`Failed to attach file: ${error.message}`, {
        duration: 5000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
    }
  }

  /**
   * Handle AI search API call
   */
  const handleAISearch = async () => {
    if (!aiQuery.trim()) return

    setIsLoadingAI(true)
    try {
      // Get user's AI configuration
      const aiConfig = await authAPI.getAIConfiguration()
      
      console.log('Using AI configuration:', {
        url: aiConfig.aiUrl,
        model: aiConfig.aiModel,
        hasToken: !!aiConfig.aiToken
      })

      const response = await fetch(aiConfig.aiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': aiConfig.aiToken
        },
        body: JSON.stringify({
          model: aiConfig.aiModel,
          messages: [
            {
              role: 'user',
              content: aiQuery
            }
          ],
          max_tokens: 500,
          temperature: 0.7
        })
      })

      if (!response.ok) {
        throw new Error(`AI API request failed: ${response.status}`)
      }

      const data = await response.json()
      const content = data.choices?.[0]?.message?.content || 'No response received'
      
      setAiResponse(content)
      setShowAIResponse(true)
      
    } catch (error) {
      console.error('AI search failed:', error)
      if (error.message.includes('AI API request failed')) {
        toast.error('AI service is unavailable. Please check your configuration in Profile settings.')
      } else if (error.message.includes('Failed to fetch')) {
        toast.error('Network error. Please check your internet connection.')
      } else {
        toast.error('AI search failed. Please try again.')
      }
    } finally {
      setIsLoadingAI(false)
    }
  }

  /**
   * Handle pasting AI response into editor
   */
  const handlePasteAIResponse = () => {
    const quill = quillRef.current?.getEditor()
    if (quill && aiResponse) {
      let insertPosition = quill.getLength() // fallback to end
      
      if (savedLineNumber !== null && savedLinePosition !== null) {
        try {
          const lines = quill.getLines()
          console.log(`Restoring to line ${savedLineNumber}, position ${savedLinePosition}`)
          
          if (savedLineNumber < lines.length) {
            // Get the target line
            const targetLine = lines[savedLineNumber]
            const lineStart = quill.getIndex(targetLine)
            const lineLength = targetLine.length()
            
            // Calculate insert position, making sure we don't exceed line length
            const positionInLine = Math.min(savedLinePosition, lineLength - 1)
            insertPosition = lineStart + positionInLine
            
            console.log(`Line start: ${lineStart}, Line length: ${lineLength}, Insert position: ${insertPosition}`)
          } else {
            console.log('Saved line number exceeds current line count, inserting at end')
          }
        } catch (error) {
          console.log('Error restoring cursor position:', error)
          insertPosition = quill.getLength()
        }
      }
      
      console.log(`Inserting AI response at position: ${insertPosition}`)
      quill.insertText(insertPosition, '\n' + aiResponse, 'user')
      quill.setSelection(insertPosition + aiResponse.length + 1)
    }
    handleCloseAIModal()
    toast.success('AI response pasted successfully!')
  }

  /**
   * Open AI modal and save cursor line position
   */
  const handleOpenAIModal = () => {
    // Check if in local mode and show message
    if (isLocalMode) {
      toast.error('AI features only available once you Sign up', {
        duration: 4000,
        icon: '🤖'
      })
      return
    }
    
    const quill = quillRef.current?.getEditor()
    if (quill) {
      const currentSelection = quill.getSelection()
      if (currentSelection) {
        // Get the line information for the current cursor position
        const [line, ] = quill.getLine(currentSelection.index)
        const lines = quill.getLines()
        const lineNumber = lines.indexOf(line)
        const lineStart = quill.getIndex(line)
        const positionInLine = currentSelection.index - lineStart
        
        console.log(`AI Modal opened - Line: ${lineNumber}, Position in line: ${positionInLine}, Total index: ${currentSelection.index}`)
        
        setSavedLineNumber(lineNumber)
        setSavedLinePosition(positionInLine)
      } else {
        // Fallback to end of document
        const lines = quill.getLines()
        setSavedLineNumber(lines.length - 1)
        setSavedLinePosition(0)
        console.log('AI Modal opened - No selection, defaulting to last line')
      }
    }
    setIsAIModalOpen(true)
  }

  /**
   * Close AI modal and reset state
   */
  const handleCloseAIModal = () => {
    setIsAIModalOpen(false)
    setAiQuery('')
    setAiResponse('')
    setShowAIResponse(false)
    setIsLoadingAI(false)
    setSavedLineNumber(null)
    setSavedLinePosition(null)
  }

  /**
   * Handle Enter key in AI modal textarea
   */
  const handleAIKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAISearch()
    }
  }

  /**
   * Generate filename for drawing
   */
  const generateDrawingFilename = (noteTitle) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    const cleanTitle = noteTitle?.replace(/[^a-zA-Z0-9]/g, '_') || 'drawing'
    return `${cleanTitle}_${timestamp}.png`
  }

  /**
   * Open drawing modal
   */
  const handleOpenDrawingModal = () => {
    // Check if in local mode and show message
    if (isLocalMode) {
      toast.error('Drawing features only available once you Sign up', {
        duration: 4000,
        icon: '🎨'
      })
      return
    }
    
    setIsDrawingModalOpen(true)
  }

  /**
   * Close drawing modal
   */
  const handleCloseDrawingModal = () => {
    setIsDrawingModalOpen(false)
    setEditingImage(null)
  }

  /**
   * Open audio recording modal
   */
  const handleOpenAudioModal = () => {
    // Check if in local mode and show message
    if (isLocalMode) {
      toast.error('Audio recording features only available once you Sign up', {
        duration: 4000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
      return
    }
    
    setIsAudioModalOpen(true)
  }

  /**
   * Close audio recording modal
   */
  const handleCloseAudioModal = () => {
    setIsAudioModalOpen(false)
  }

  /**
   * Handle drawing completion
   */
  const handleDrawingDone = async (canvasDataUrl) => {
    try {
      // Get current cursor position
      const quill = quillRef.current?.getEditor()
      if (!quill) {
        throw new Error('Editor not available')
      }
      
      const range = quill.getSelection() || { index: quill.getLength() }
      
      // Insert image immediately into editor
      const imageHtml = `<img src="${canvasDataUrl}" alt="Drawing" style="max-width: 100%; height: auto;" />`
      quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml, 'user')
      quill.setSelection(range.index + 1)
      
      // Close modal immediately for responsive UX
      handleCloseDrawingModal()
      
      // Show success message
      toast.success('Drawing added to note!', {
        duration: 2000,
        style: {
          background: 'rgba(16, 185, 129, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }
      })
      
      // Upload in background (don't await)
      uploadDrawingInBackground(canvasDataUrl, range.index)
      
    } catch (error) {
      console.error('Failed to save drawing:', error)
      toast.error('Failed to save drawing')
    }
  }

  /**
   * Handle audio recording completion
   */
  const handleAudioDone = async (audioFile) => {
    try {
      // Show loading spinner
      const spinnerToast = toast.loading(
        'Processing audio recording...', 
        {
          duration: Infinity,
          style: {
            background: 'rgba(31, 41, 55, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }
      )

      // Get current cursor position
      const quill = quillRef.current?.getEditor()
      if (!quill) {
        throw new Error('Editor not available')
      }
      
      const range = quill.getSelection() || { index: quill.getLength() }
      
      // Log upload details for debugging
      console.log('🎵 Uploading audio file:', {
        name: audioFile.name,
        size: audioFile.size,
        type: audioFile.type,
        isLocalMode: isLocalMode
      })
      
      // Upload audio file
      let uploadResult
      if (isLocalMode) {
        console.log('🎵 Using local mode upload')
        uploadResult = await uploadFileLocal(audioFile)
      } else {
        console.log('🎵 Using cloud attachment upload')
        // Use attachment upload endpoint for audio files to save to Google Drive attachments folder
        uploadResult = await uploadAttachment(audioFile)
        
        // Cache the attachment locally for offline access (same process as other attachments)
        if (uploadResult && uploadResult.success) {
          console.log('🎵 Caching attachment locally for offline access')
          try {
            // Create a new File object with the Google Drive filename for caching
            const cachedFile = new File([audioFile], uploadResult.actualFileName || uploadResult.fileName, {
              type: audioFile.type,
              lastModified: audioFile.lastModified
            })
            await localStorageService.saveCloudAttachment(cachedFile, uploadResult.id)
            console.log('🎵 Attachment cached successfully with filename:', uploadResult.actualFileName || uploadResult.fileName)
          } catch (cacheError) {
            console.warn('⚠️ Failed to cache attachment locally:', cacheError)
            // Don't fail the upload if caching fails
          }
        }
      }
      
      console.log('🎵 Upload result:', uploadResult)

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || 'Upload failed - no result received')
      }

      // Create attachment link HTML with fallback handling
      let attachmentHtml
      const fileName = uploadResult.fileName || audioFile.name
      const fileId = uploadResult.id || uploadResult.fileId || uploadResult.attachmentId
      
      if (isLocalMode || !uploadResult.cloudFileId) {
        // Local file or fallback to local
        attachmentHtml = `<a href="javascript:void(0)" title="Download ${fileName}" data-file-id="${fileId}" data-file-name="${fileName}" data-download-url="local://${fileId}" data-handler-attached="true" data-local-file="true" style="color: #3b82f6; text-decoration: underline; cursor: pointer;">🎵 ${fileName}</a>`
      } else {
        // Cloud file - use the same pattern as regular file attachments
        const cloudFileId = uploadResult.cloudFileId || fileId
        // Store the actual Google Drive filename for searching, but display the original filename
        const actualFileName = uploadResult.actualFileName || fileName
        attachmentHtml = `<a href="javascript:void(0)" title="Download ${fileName}" data-file-id="${cloudFileId}" data-file-name="${actualFileName}" data-original-file-name="${fileName}" data-download-url="gdrive://${cloudFileId}" data-handler-attached="true" data-cloud-file="true" style="color: #3b82f6; text-decoration: underline; cursor: pointer;">🎵 ${fileName}</a>`
      }
      
      console.log('🎵 Creating attachment link:', {
        fileName,
        fileId,
        cloudFileId: uploadResult.cloudFileId,
        isLocalMode,
        uploadResultKeys: Object.keys(uploadResult)
      })
      
      console.log('🎵 Attachment HTML to be inserted:', attachmentHtml)
      
      // Create the link element directly to preserve attributes
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = attachmentHtml
      const linkElement = tempDiv.firstChild
      
      // Insert text first, then apply link format
      const linkText = `🎵 ${fileName}`
      quill.insertText(range.index, linkText)
      
      // Apply link format and set custom attributes
      const linkRange = { index: range.index, length: linkText.length }
      quill.formatText(linkRange.index, linkRange.length, 'link', 'javascript:void(0)')
      
      // Manually set the attributes on the created link element
      setTimeout(() => {
        const editorElement = quill.container.querySelector('.ql-editor')
        const allLinks = editorElement.querySelectorAll('a')
        const targetLink = Array.from(allLinks).find(link => 
          link.textContent === linkText && link.href === 'javascript:void(0)'
        )
        
        if (targetLink) {
          // Copy all attributes from our prepared link element
          Array.from(linkElement.attributes).forEach(attr => {
            targetLink.setAttribute(attr.name, attr.value)
          })
          console.log('🎵 Link attributes set successfully:', Array.from(targetLink.attributes).map(attr => `${attr.name}="${attr.value}"`).join(', '))
        }
        
        // Process the newly added link
        processExistingAttachmentLinks()
      }, 100)
      
      // Close modal immediately for responsive UX
      handleCloseAudioModal()
      
      // Dismiss spinner and show success
      toast.dismiss(spinnerToast)
      toast.success('Audio recording attached successfully!', {
        duration: 3000,
        style: {
          background: 'rgba(16, 185, 129, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }
      })
      
    } catch (error) {
      console.error('❌ Failed to save audio recording:', error)
      toast.dismiss()
      toast.error(`Failed to save audio recording: ${error.message}`, {
        duration: 5000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
    }
  }

  /**
   * Attach resize handles to all images in the editor
   */
  const attachResizeHandlesToImages = useCallback(() => {
    // Temporary disable to prevent Quill conflicts
    if (!ENABLE_IMAGE_RESIZE) {
      console.log('🔍 RichTextEditor: Image resize disabled to prevent conflicts')
      return
    }
    
    try {
      const editor = quillRef.current?.getEditor()
      if (!editor || !editor.root) return

      // Check if Quill is in a stable state
      if (!editor.scroll || !editor.scroll.domNode) {
        console.log('🔍 RichTextEditor: Quill not ready for resize handles, skipping...')
        return
      }

      const images = editor.root.querySelectorAll('img')
      console.log(`🔍 RichTextEditor: Attaching resize handles to ${images.length} images`)
      
      images.forEach((img, index) => {
        // Skip if already has click handler
        if (img.hasAttribute('data-resize-enabled')) return
        
        // Skip if image is not fully loaded
        if (!img.complete || !img.naturalWidth) {
          console.log(`🔍 RichTextEditor: Skipping image ${index + 1} - not fully loaded`)
          return
        }
        
        // Add click handler for on-demand resize handles
        img.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          handleImageClick(img)
        })
        
        // Mark as having click handler
        img.setAttribute('data-resize-enabled', 'true')
        
        console.log(`🔍 RichTextEditor: Added click handler to image ${index + 1}`)
      })
    } catch (error) {
      console.warn('⚠️ RichTextEditor: Error attaching resize handles:', error)
    }
  }, [])

  /**
   * Handle image click and open resize modal
   */
  const handleImageClick = useCallback((img) => {
    try {
      if (USE_MODAL_RESIZE) {
        // Modal-based resize (safe approach)
        const rect = img.getBoundingClientRect()
        const currentWidth = rect.width
        const currentHeight = rect.height
        
        setResizeImageData({
          element: img,
          currentWidth: Math.round(currentWidth),
          currentHeight: Math.round(currentHeight)
        })
        setIsImageResizeModalOpen(true)
        
        console.log(`🔍 RichTextEditor: Opening resize modal for image (${currentWidth}×${currentHeight})`)
      } else {
        // DOM-based resize (disabled due to Quill conflicts)
        console.warn('⚠️ RichTextEditor: DOM-based resize disabled due to Quill conflicts')
      }
    } catch (error) {
      console.warn('⚠️ RichTextEditor: Error handling image click:', error)
    }
  }, [USE_MODAL_RESIZE])

  /**
   * Handle image selection and show resize handles
   */
  const handleImageSelect = useCallback((imgElement, wrapper) => {
    // Deselect previous image
    if (selectedImage && selectedImage !== imgElement) {
      const prevWrapper = selectedImage.closest('.image-with-resize')
      if (prevWrapper) {
        const handles = prevWrapper.querySelector('.resize-handles-container')
        if (handles) handles.remove()
      }
    }
    
    setSelectedImage(imgElement)
    setResizeHandlesVisible(true)
    
    // Create resize handles container
    let handlesContainer = wrapper.querySelector('.resize-handles-container')
    if (!handlesContainer) {
      handlesContainer = document.createElement('div')
      handlesContainer.className = 'resize-handles-container absolute inset-0 pointer-events-none'
      handlesContainer.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
      `
      wrapper.appendChild(handlesContainer)
      
      // Render React resize handles
      const root = createRoot(handlesContainer)
      root.render(
        React.createElement(ImageResizeHandles, {
          imageElement: imgElement,
          onResize: handleImageResize,
          onResizeComplete: handleImageResizeComplete,
          isVisible: true
        })
      )
    }
  }, [selectedImage])

  /**
   * Handle real-time image resize
   */
  const handleImageResize = useCallback((width, height) => {
    // Real-time resize feedback is handled by the ImageResizeHandles component
    // This callback can be used for additional real-time updates if needed
  }, [])

  /**
   * Handle image resize completion
   */
  const handleImageResizeComplete = useCallback((width, height) => {
    if (selectedImage) {
      // Update image attributes for persistence
      selectedImage.setAttribute('width', width)
      selectedImage.setAttribute('height', height)
      
      // Also update the style for immediate visual feedback
      selectedImage.style.width = `${width}px`
      selectedImage.style.height = `${height}px`
      selectedImage.style.maxWidth = '100%'
      
      // Trigger content change for auto-save
      const editor = quillRef.current?.getEditor()
      if (editor) {
        editor.root.dispatchEvent(new Event('input', { bubbles: true }))
      }
      
      toast.success(`Image resized to ${Math.round(width)}×${Math.round(height)}px`, {
        duration: 2000,
        style: {
          background: 'rgba(16, 185, 129, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }
      })
    }
  }, [selectedImage])

  /**
   * Handle image resize modal close
   */
  const handleCloseImageResizeModal = useCallback(() => {
    setIsImageResizeModalOpen(false)
    setResizeImageData(null)
  }, [])

  /**
   * Handle image resize apply
   */
  const handleImageResizeApply = useCallback((newWidth, newHeight) => {
    if (resizeImageData && resizeImageData.element) {
      const img = resizeImageData.element
      
      // IMPORTANT: Preserve the data-google-drive-url attribute for proper saving
      const googleDriveUrl = img.getAttribute('data-google-drive-url')
      
      // Update image attributes for persistence (non-destructive)
      img.setAttribute('width', newWidth)
      img.setAttribute('height', newHeight)
      
      // Update style for immediate visual feedback
      img.style.width = `${newWidth}px`
      img.style.height = `${newHeight}px`
      img.style.maxWidth = '100%'
      
      // Ensure the Google Drive URL attribute is preserved
      if (googleDriveUrl) {
        img.setAttribute('data-google-drive-url', googleDriveUrl)
        console.log('🔗 Preserved Google Drive URL attribute during resize:', googleDriveUrl)
      }
      
      // Log current image state for debugging
      console.log(`🔍 RichTextEditor: Image after resize:`, {
        src: img.src.substring(0, 50) + '...',
        width: img.getAttribute('width'),
        height: img.getAttribute('height'),
        hasGoogleDriveUrl: img.hasAttribute('data-google-drive-url'),
        googleDriveUrl: img.getAttribute('data-google-drive-url')
      })
      
      // Trigger content change for auto-save
      const editor = quillRef.current?.getEditor()
      if (editor) {
        editor.root.dispatchEvent(new Event('input', { bubbles: true }))
      }
      
      toast.success(`Image resized to ${newWidth}×${newHeight}px`, {
        duration: 2000,
        style: {
          background: 'rgba(16, 185, 129, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }
      })
      
      console.log(`🔍 RichTextEditor: Image resized via modal to ${newWidth}×${newHeight}px`)
    }
  }, [resizeImageData])

  /**
   * Upload drawing in background and update the image source
   */
  const uploadDrawingInBackground = async (canvasDataUrl, imageIndex) => {
    try {
      // Show background upload indicator
      const uploadToast = toast.loading('Uploading drawing to cloud...', {
        style: {
          background: 'rgba(59, 130, 246, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(59, 130, 246, 0.3)'
        }
      })

      // Convert canvas data URL to blob
      const response = await fetch(canvasDataUrl)
      const blob = await response.blob()
      
      // Create file object
      const file = new File([blob], generateDrawingFilename(noteTitle), {
        type: 'image/png'
      })
      
      // Upload to cloud storage
      const uploadResult = await uploadImageFile(file)
      
      // Update the image in the editor with the cloud URL
      if (uploadResult && uploadResult.googleDriveUrl) {
        const quill = quillRef.current?.getEditor()
        if (quill) {
          // Find the image we just inserted and update its src
          const images = quill.root.querySelectorAll('img')
          if (images[imageIndex]) {
            const img = images[imageIndex]
            if (img.src === canvasDataUrl) {
              img.src = uploadResult.googleDriveUrl
              img.setAttribute('data-google-drive-url', uploadResult.googleDriveUrl)
              console.log('🖼️ Drawing uploaded and URL updated:', uploadResult.googleDriveUrl)
            }
          }
        }
      }
      
      // Dismiss upload toast and show success
      toast.dismiss(uploadToast)
      toast.success('Drawing uploaded to cloud!', {
        duration: 2000,
        style: {
          background: 'rgba(16, 185, 129, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }
      })
      
    } catch (error) {
      console.error('Failed to upload drawing in background:', error)
      
      // Show error but don't disrupt the user experience
      toast.error('Drawing saved locally, cloud upload failed', {
        duration: 3000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
    }
  }

  /**
   * Handle mention button click - opens note selection modal
   */
  const handleMentionButtonClick = useCallback((atIndex, partialText = '') => {
    console.log(`🔗 RichTextEditor: Opening note link modal at index ${atIndex} with partial text: "${partialText}"`)
    console.log(`🔗 RichTextEditor: Mobile device: ${window.innerWidth < 768}`)
    console.log(`🔗 RichTextEditor: Modal state before opening:`, isNoteLinkModalOpen)
    
    setMentionCursorIndex(atIndex)
    setMentionPartialText(partialText)
    setIsNoteLinkModalOpen(true)
    
    // Verify state was set
    setTimeout(() => {
      console.log(`🔗 RichTextEditor: Modal state after opening:`, isNoteLinkModalOpen)
    }, 10)
  }, [isNoteLinkModalOpen])

  /**
   * Handle note selection from modal - insert note link
   */
  const handleNoteSelected = useCallback((noteData) => {
    console.log(`🔗 RichTextEditor: Note selected:`, noteData)
    
    const quill = quillRef.current?.getEditor()
    if (!quill || mentionCursorIndex === null) {
      console.warn('⚠️ RichTextEditor: No quill editor or cursor index available')
      return
    }

    // Insert the note link directly
    const replaceLength = 1 + mentionPartialText.length // @ + partial text
    
    // Delete the @ and partial text
    quill.deleteText(mentionCursorIndex, replaceLength)
    
    // Insert the note link
    const linkText = `@${noteData.title}`
    quill.insertText(mentionCursorIndex, linkText, 'noteLink', noteData)
    
    // Add a newline after the link to ensure proper separation
    quill.insertText(mentionCursorIndex + linkText.length, '\n')
    
    // Position cursor after the newline and clear all formatting
    const cursorPos = mentionCursorIndex + linkText.length + 1
    quill.setSelection(cursorPos)
    
    // Clear all formatting to ensure normal text continues
    setTimeout(() => {
      quill.setSelection(cursorPos)
      
      // Clear all possible formats at cursor position
      const currentFormats = quill.getFormat(cursorPos)
      console.log('🔍 RichTextEditor: Current formats at cursor:', currentFormats)
      
      // Remove any lingering formats
      Object.keys(currentFormats).forEach(format => {
        if (format !== 'list' && format !== 'header') { // Keep structural formats
          quill.format(format, false)
        }
      })
      
      // Explicitly clear note link format
      quill.format('noteLink', false)
      
      console.log('🧹 RichTextEditor: Cleared all formatting after note link insertion')
      
      // Verify formatting is cleared
      setTimeout(() => {
        const finalFormats = quill.getFormat(cursorPos)
        console.log('🔍 RichTextEditor: Final formats at cursor:', finalFormats)
      }, 50)
    }, 10)
    
    // Trigger onChange to save the change
    const newContent = quill.root.innerHTML
    onChange(newContent)
    
    toast.success(`Linked to "${noteData.title}"`, {
      duration: 2000,
      style: {
        background: 'rgba(16, 185, 129, 0.5)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: 'white',
        borderRadius: '12px',
        padding: '12px 16px',
        fontSize: '14px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        border: '1px solid rgba(16, 185, 129, 0.3)'
      }
    })
    
    console.log(`🔗 RichTextEditor: Inserted link to "${noteData.title}" at position ${mentionCursorIndex}`)

    // Reset state
    setMentionCursorIndex(null)
    setMentionPartialText('')
  }, [mentionCursorIndex, mentionPartialText, onChange])

  /**
   * Handle note link modal close
   */
  const handleNoteLinkModalClose = useCallback(() => {
    setIsNoteLinkModalOpen(false)
    setMentionCursorIndex(null)
    setMentionPartialText('')
  }, [])

  // Process image references in content to convert them back to displayable images (async version)
  const processImageReferences = async (content) => {
    if (!content || !content.includes('image://')) {
      return content
    }
    
    // Find all image references
    const imageRefRegex = /<img[^>]+src="image:\/\/[^"]+"[^>]*>/g
    const matches = content.match(imageRefRegex)
    
    if (!matches) {
      return content
    }
    
    let processedContent = content
    
    // Process each image reference
    for (const match of matches) {
      const srcMatch = match.match(/src="image:\/\/[^"]+"/)
      if (srcMatch) {
        const imageRef = srcMatch[1]
        console.log('🖼️ RichTextEditor: Processing image reference:', imageRef)
        
        // For now, replace with a placeholder since we don't have the actual image data
        // In a real implementation, you'd want to store a mapping between references and actual file IDs
        const placeholder = '<div class="image-placeholder bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center text-gray-500 dark:text-gray-400 text-sm">[Image - Cloud Reference]</div>'
        processedContent = processedContent.replace(match, placeholder)
      }
    }
    
    return processedContent
  }

  // Process image references in content to convert them back to displayable images (async version)
  const processImageReferencesAsync = async (content) => {
    if (!content) {
      return content
    }
    
    console.log('🖼️ RichTextEditor: Processing image references in content:', content.substring(0, 200) + '...')
    
    // Find all img tags in the content to analyze what we have
    const allImgRegex = /<img[^>]+src="([^"]+)"[^>]*>/g
    const allMatches = [...content.matchAll(allImgRegex)]
    console.log(`🖼️ RichTextEditor: Found ${allMatches.length} total images in content`)
    
    // Log what types of images we have
    allMatches.forEach((match, index) => {
      const src = match[1]
      let type = 'unknown'
      if (src.startsWith('data:image/')) {
        type = 'base64'
      } else if (src.includes('drive.google.com')) {
        type = 'google-drive'
      } else if (src.startsWith('http')) {
        type = 'external-url'
      }
      console.log(`🖼️ RichTextEditor: Image ${index + 1}: ${type} - ${src.substring(0, 50)}...`)
    })
    
    // For cloud mode, convert Google Drive URLs to base64 images for display
    if (!isLocalMode) {
      try {
        const { getImageBase64, extractFileIdFromUrl } = await import('../../services/imageUploadService')
        
        // Find all Google Drive image URLs in the content
        const googleDriveUrlRegex = /<img[^>]+src="(https:\/\/drive\.google\.com\/file\/d\/[^"]+)"[^>]*>/g
        const matches = [...content.matchAll(googleDriveUrlRegex)]
        
        if (matches.length === 0) {
          console.log('🖼️ RichTextEditor: No Google Drive images found in content - returning content as-is')
          return content
        }
        
        console.log(`🖼️ RichTextEditor: Found ${matches.length} Google Drive images to process`)
        
        let processedContent = content
        
        // Process each Google Drive URL
        for (const match of matches) {
          const googleDriveUrl = match[1]
          const fileId = extractFileIdFromUrl(googleDriveUrl)
          
          if (fileId) {
            try {
              const base64DataUrl = await getImageBase64(googleDriveUrl, fileId)
              if (base64DataUrl) {
                // Replace the Google Drive URL with base64 data URL AND add data attribute
                // This ensures the original URL is preserved for save-time conversion
                // IMPORTANT: Preserve all existing attributes including width/height
                const imgTagRegex = new RegExp(`<img([^>]+)src="${googleDriveUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"([^>]*)>`, 'g')
                processedContent = processedContent.replace(imgTagRegex, (match, beforeSrc, afterSrc) => {
                  // Extract width and height attributes if they exist
                  const widthMatch = match.match(/width="([^"]*)"/)
                  const heightMatch = match.match(/height="([^"]*)"/)
                  const styleMatch = match.match(/style="([^"]*)"/)
                  
                  let preservedAttributes = ''
                  if (widthMatch) preservedAttributes += ` width="${widthMatch[1]}"`
                  if (heightMatch) preservedAttributes += ` height="${heightMatch[1]}"`
                  if (styleMatch) preservedAttributes += ` style="${styleMatch[1]}"`
                  
                  console.log(`🔍 RichTextEditor: Preserving attributes for image:`, {
                    width: widthMatch ? widthMatch[1] : 'none',
                    height: heightMatch ? heightMatch[1] : 'none',
                    style: styleMatch ? styleMatch[1] : 'none'
                  })
                  
                  return `<img${beforeSrc}src="${base64DataUrl}" data-google-drive-url="${googleDriveUrl}"${preservedAttributes}${afterSrc}>`
                })
                console.log(`🖼️ RichTextEditor: Converted Google Drive URL to base64 with data attribute: ${fileId}`)
              } else {
                console.warn(`⚠️ RichTextEditor: No base64 data returned for Google Drive URL: ${fileId} - keeping original URL`)
              }
            } catch (error) {
              console.warn(`⚠️ RichTextEditor: Failed to convert Google Drive URL to base64: ${fileId} - keeping original URL`, error)
            }
          } else {
            console.warn(`⚠️ RichTextEditor: Could not extract file ID from Google Drive URL: ${googleDriveUrl}`)
          }
        }
        
        console.log('🖼️ RichTextEditor: Processed content:', processedContent.substring(0, 200) + '...')
        return processedContent
      } catch (error) {
        console.error('❌ RichTextEditor: Error processing image references:', error)
        return content
      }
    }
    
    // For local mode, content should already have data URLs
    console.log('🖼️ RichTextEditor: Local mode - content should already have data URLs')
    return content
  }

  // Get plain text length from HTML content - OPTIMIZED
  const getPlainTextLength = useCallback((content) => {
    if (!content) return 0
    
    // Use regex to strip HTML tags instead of DOM parsing - much faster
    const strippedContent = content
      .replace(/<img[^>]+src="image:\/\/[^"]+"[^>]*>/g, '[Image]')  // Replace image refs
      .replace(/<[^>]*>/g, '')  // Strip all HTML tags
      .replace(/&nbsp;/g, ' ')  // Replace HTML entities
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
    
    return strippedContent.length
  }, [])

  // Debounced character count calculation to prevent lag during typing
  const [currentLength, setCurrentLength] = useState(0)
  const [isNearLimit, setIsNearLimit] = useState(false)
  const [isOverLimit, setIsOverLimit] = useState(false)
  
  // Debounce character count calculation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const length = getPlainTextLength(value)
      setCurrentLength(length)
      setIsNearLimit(maxLength ? length > maxLength * 0.9 : false)
      setIsOverLimit(maxLength ? length > maxLength : false)
    }, 200) // 200ms debounce for character count
    
    return () => clearTimeout(timeoutId)
  }, [value, maxLength, getPlainTextLength])
  
  // Handle content change with character limit validation - OPTIMIZED
  const handleChange = (content) => {
    // Skip if this is the same content we just set (prevents infinite loop)
    if (lastSetValue.current === content) {
      return
    }
    
    // Update the last set value to prevent external updates from overriding user input
    lastSetValue.current = content
    
    // PERFORMANCE FIX: Always call onChange immediately, character validation is debounced
    onChange(content)
    
    // Character limit validation is now handled by the debounced useEffect above
    // This prevents expensive regex operations on every keystroke
  }

  // Comprehensive toolbar configuration
  const modules = {
    toolbar: isFullscreen ? [
      // Single-line compact toolbar for fullscreen mode - Only standard Quill buttons
      ['bold', 'italic', 'underline', { 'list': 'ordered'}, { 'list': 'bullet' }, 'link', 'blockquote', 'clean']
    ] : [
      // Full toolbar for normal mode - Only standard Quill buttons
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'blockquote', 'code-block'],
      ['clean']
    ],
    history: {
      delay: 2000,
      maxStack: 500,
      userOnly: true
    },
    // Removed quill-table-up module configuration
  }

  // Handle keyboard shortcuts including table operations
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Table-specific keyboard shortcuts
      if (event.target.tagName === 'TD') {
        const table = event.target.closest('table')
        if (table) {
          const cell = event.target
          const rowIndex = parseInt(cell.parentElement.dataset.rowIndex)
          const colIndex = parseInt(cell.dataset.colIndex)
          
          if (event.ctrlKey || event.metaKey) {
            switch (event.key) {
              case 'ArrowUp':
                event.preventDefault()
                if (event.shiftKey) {
                  // Add row above
                  SimpleTableBlot.addRow(table, rowIndex)
                }
                break
              case 'ArrowDown':
                event.preventDefault()
                if (event.shiftKey) {
                  // Add row below
                  SimpleTableBlot.addRow(table, rowIndex + 1)
                }
                break
              case 'ArrowLeft':
                event.preventDefault()
                if (event.shiftKey) {
                  // Add column left
                  SimpleTableBlot.addColumn(table, colIndex)
                }
                break
              case 'ArrowRight':
                event.preventDefault()
                if (event.shiftKey) {
                  // Add column right
                  SimpleTableBlot.addColumn(table, colIndex + 1)
                }
                break
              case 'Delete':
              case 'Backspace':
                event.preventDefault()
                if (event.shiftKey) {
                  // Delete row
                  const totalRows = table.querySelectorAll('tr').length
                  if (totalRows > 1) {
                    SimpleTableBlot.deleteRow(table, rowIndex)
                  }
                } else {
                  // Delete column
                  const totalCols = table.querySelectorAll('tr:first-child td').length
                  if (totalCols > 1) {
                    SimpleTableBlot.deleteColumn(table, colIndex)
                  }
                }
                break
            }
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Fix Quill dropdown positioning
  useEffect(() => {
    const fixDropdownPositioning = () => {
      const editor = quillRef.current?.getEditor?.()
      if (!editor) return

      const toolbar = editor.container.querySelector('.ql-toolbar')
      if (!toolbar) return

      // Find all picker dropdowns
      const dropdowns = toolbar.querySelectorAll('.ql-picker-options')
      
      dropdowns.forEach(dropdown => {
        // Set up initial positioning - will be updated on click
        dropdown.style.position = 'fixed'
        dropdown.style.zIndex = '999999'
        dropdown.style.background = 'white'
        dropdown.style.border = '1px solid #ccc'
        dropdown.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
        dropdown.style.maxHeight = '200px'
        dropdown.style.overflowY = 'auto'
        dropdown.style.minWidth = '120px'
        dropdown.style.transform = 'none'
        dropdown.style.margin = '0'
        dropdown.style.padding = '0'
        dropdown.style.width = 'auto'
      })

      // Set up mutation observer to fix new dropdowns
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const dropdowns = node.querySelectorAll ? node.querySelectorAll('.ql-picker-options') : []
                dropdowns.forEach(dropdown => {
                  dropdown.style.position = 'fixed'
                  dropdown.style.zIndex = '999999'
                  dropdown.style.background = 'white'
                  dropdown.style.border = '1px solid #ccc'
                  dropdown.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
                  dropdown.style.maxHeight = '200px'
                  dropdown.style.overflowY = 'auto'
                  dropdown.style.minWidth = '120px'
                  dropdown.style.transform = 'none'
                  dropdown.style.margin = '0'
                  dropdown.style.padding = '0'
                  dropdown.style.width = 'auto'
                })
              }
            })
          }
        })
      })

      observer.observe(toolbar, { childList: true, subtree: true })

      return () => observer.disconnect()
    }

    const timer = setTimeout(fixDropdownPositioning, 100)
    return () => clearTimeout(timer)
  }, [value]) // Re-run when content changes

  // Handle dropdown visibility and positioning
  useEffect(() => {
    const handleDropdownToggle = () => {
      const editor = quillRef.current?.getEditor?.()
      if (!editor) return

      const toolbar = editor.container.querySelector('.ql-toolbar')
      if (!toolbar) return

      // Find all picker buttons
      const pickerButtons = toolbar.querySelectorAll('.ql-picker')
      
      pickerButtons.forEach(button => {
        // Remove any existing listeners to avoid duplicates
        button.removeEventListener('click', handlePickerClick)
        button.addEventListener('click', handlePickerClick)
      })

      function handlePickerClick() {
        setTimeout(() => {
          const dropdown = this.querySelector('.ql-picker-options')
          if (dropdown) {
            // Get the button's position relative to the viewport
            const buttonRect = this.getBoundingClientRect()
            
            // Position dropdown below the button
            dropdown.style.position = 'fixed'
            dropdown.style.top = `${buttonRect.bottom + window.scrollY}px`
            dropdown.style.left = `${buttonRect.left + window.scrollX}px`
            dropdown.style.zIndex = '999999'
            dropdown.style.background = 'white'
            dropdown.style.border = '1px solid #ccc'
            dropdown.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
            dropdown.style.maxHeight = '200px'
            dropdown.style.overflowY = 'auto'
            dropdown.style.minWidth = '120px'
            dropdown.style.transform = 'none'
            dropdown.style.margin = '0'
            dropdown.style.padding = '0'
            dropdown.style.width = 'auto'
            
            // Dark mode support
            if (document.documentElement.classList.contains('dark')) {
              dropdown.style.background = '#374151'
              dropdown.style.borderColor = '#4b5563'
              dropdown.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)'
            }
            
            // AMOLED mode support
            if (document.documentElement.classList.contains('amoled')) {
              dropdown.style.background = '#000000'
              dropdown.style.borderColor = '#171717'
              dropdown.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.5)'
            }
          }
        }, 10)
      }
    }

    const timer = setTimeout(handleDropdownToggle, 200)
    return () => clearTimeout(timer)
  }, [value])

  // Setup custom handlers after editor is ready
  useEffect(() => {
    let pasteHandler = null
    
    const timer = setTimeout(() => {
      const editor = quillRef.current?.getEditor?.()
      console.log('Editor found:', !!editor)
      if (editor) {
        const toolbar = editor.getModule('toolbar')
        console.log('Toolbar found:', !!toolbar)
        if (toolbar) {
          // Add custom image button to toolbar
          const toolbarContainer = editor.container.querySelector('.ql-toolbar')
          if (toolbarContainer) {
            const imageButton = document.createElement('button')
            imageButton.type = 'button'
            imageButton.className = 'ql-image'
            imageButton.innerHTML = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="10" width="12" x="3" y="4"></rect><circle class="ql-fill" cx="6" cy="7" r="1"></circle><polyline class="ql-even ql-fill" points="5,12 5,11 7,9 8,10 11,7 11,12 5,12"></polyline></svg>'
            imageButton.title = 'Insert Image'
            
            // Add explicit styling to ensure visibility
            imageButton.style.cssText = `
              display: inline-block;
              width: 28px;
              height: 28px;
              padding: 4px;
              margin: 0 1px;
              border: 1px solid transparent;
              border-radius: 3px;
              background: transparent;
              cursor: pointer;
              vertical-align: middle;
              color: #444;
            `
            imageButton.style.color = '#444'
            
            // Add hover effects
            imageButton.addEventListener('mouseenter', () => {
              imageButton.style.backgroundColor = '#f0f0f0'
              imageButton.style.borderColor = '#ccc'
            })
            
            imageButton.addEventListener('mouseleave', () => {
              imageButton.style.backgroundColor = 'transparent'
              imageButton.style.borderColor = 'transparent'
            })
            
            imageButton.addEventListener('click', (e) => {
              e.preventDefault()
              console.log('🔍 Custom image button clicked!')
            const input = document.createElement('input')
            input.setAttribute('type', 'file')
            input.setAttribute('accept', 'image/*')
            input.style.display = 'none'
            
            input.addEventListener('change', (e) => {
                console.log('🔍 File input change event triggered')
              const file = e.target.files?.[0]
              if (file) {
                  console.log('🔍 File selected, calling handleImageUpload')
                handleImageUpload(file)
                } else {
                  console.log('🔍 No file selected')
              }
              document.body.removeChild(input)
            })
            
            document.body.appendChild(input)
            input.click()
          })
            
            // Insert the custom image button after the link button
            const linkButton = toolbarContainer.querySelector('.ql-link')
            if (linkButton && linkButton.parentNode) {
              linkButton.parentNode.insertBefore(imageButton, linkButton.nextSibling)
              console.log('✅ Custom image button added to toolbar after link button')
            } else {
              // Fallback: append to the first toolbar group
              const firstGroup = toolbarContainer.querySelector('span.ql-formats')
              if (firstGroup) {
                firstGroup.appendChild(imageButton)
                console.log('✅ Custom image button added to first toolbar group')
              } else {
                // Last resort: append directly to toolbar
                toolbarContainer.appendChild(imageButton)
                console.log('✅ Custom image button added directly to toolbar container')
              }
            }
            
            // Add some debugging to check if button is visible
            console.log('🔍 Image button added:', {
              exists: !!imageButton,
              visible: imageButton.offsetWidth > 0,
              parent: imageButton.parentNode?.className,
              position: imageButton.parentNode?.children ? Array.from(imageButton.parentNode.children).indexOf(imageButton) : -1
            })
          }
        }

        // Add paste event handler for images
        const editorContainer = editor.container
        pasteHandler = (e) => {
          const clipboardData = e.clipboardData || window.clipboardData
          if (!clipboardData) return

          const items = clipboardData.items
          if (!items) return

          // Look for image in clipboard
          for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item.type.startsWith('image/')) {
              e.preventDefault() // Prevent default paste behavior
              
              const file = item.getAsFile()
              if (file) {
                handlePastedImage(file)
              }
              break
            }
          }
        }

        editorContainer.addEventListener('paste', pasteHandler)

        // Manually add emoji button to toolbar
        console.log('Editor container:', editor.container)
        
        // Try multiple selectors to find toolbar
        let toolbarContainer = editor.container.querySelector('.ql-toolbar')
        if (!toolbarContainer) {
          toolbarContainer = editor.container.parentElement?.querySelector('.ql-toolbar')
        }
        if (!toolbarContainer) {
          toolbarContainer = document.querySelector('.ql-toolbar')
        }
        
        console.log('Toolbar container found:', !!toolbarContainer)
        
        // Create undo button if not exists
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-undo')) {
          console.log('Adding custom undo button')
          
          const undoButton = document.createElement('button')
          undoButton.type = 'button'
          undoButton.className = 'ql-undo'
          undoButton.title = 'Undo'
          undoButton.innerHTML = '<svg viewBox="0 0 18 18"><path d="M4 7.5L1.5 5L4 2.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.5 5h8.5c3 0 5.5 2.5 5.5 5.5s-2.5 5.5-5.5 5.5h-2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
          
          undoButton.addEventListener('click', (e) => {
            e.preventDefault()
            const quill = quillRef.current?.getEditor()
            if (quill) {
              quill.history.undo()
            }
          })
          
          // Insert at the beginning of the first toolbar group
          const firstToolbarGroup = toolbarContainer.querySelector('span.ql-formats')
          if (firstToolbarGroup) {
            firstToolbarGroup.insertBefore(undoButton, firstToolbarGroup.firstChild)
            console.log('Undo button inserted at beginning of toolbar')
          }
        }

        // Create redo button if not exists
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-redo')) {
          console.log('Adding custom redo button')
          
          const redoButton = document.createElement('button')
          redoButton.type = 'button'
          redoButton.className = 'ql-redo'
          redoButton.title = 'Redo'
          redoButton.innerHTML = '<svg viewBox="0 0 18 18"><path d="M14 7.5L16.5 5L14 2.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.5 5H8c-3 0-5.5 2.5-5.5 5.5S5 16 8 16h2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
          
          redoButton.addEventListener('click', (e) => {
            e.preventDefault()
            const quill = quillRef.current?.getEditor()
            if (quill) {
              quill.history.redo()
            }
          })
          
          // Insert after undo button
          const undoButton = toolbarContainer.querySelector('.ql-undo')
          if (undoButton && undoButton.parentNode) {
            undoButton.parentNode.insertBefore(redoButton, undoButton.nextSibling)
            console.log('Redo button inserted after undo button')
          }
        }

        if (toolbarContainer && !toolbarContainer.querySelector('.ql-emoji')) {
          console.log('Adding custom emoji button')
          
          // Create emoji button
          const emojiButton = document.createElement('button')
          emojiButton.type = 'button'
          emojiButton.className = 'ql-emoji'
          emojiButton.title = 'Insert Emoji'
          
          // Add emoji SVG icon
          emojiButton.innerHTML = '<svg viewBox="0 0 18 18"><circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="6.5" cy="7" r="1" fill="currentColor"/><circle cx="11.5" cy="7" r="1" fill="currentColor"/><path d="M6 11.5 Q9 13.5 12 11.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
          
          emojiButton.addEventListener('click', (e) => {
            e.preventDefault()
            setIsEmojiPickerOpen(true)
          })
          
          // Insert after image button or append to first toolbar group
          const imageButton = toolbarContainer.querySelector('.ql-image')
          if (imageButton && imageButton.parentNode) {
            imageButton.parentNode.insertBefore(emojiButton, imageButton.nextSibling)
            console.log('Emoji button inserted after image button')
          } else {
            const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
            if (toolbarGroup) {
              toolbarGroup.appendChild(emojiButton)
              console.log('Emoji button appended to toolbar group')
            }
          }
        }

        // Add scroll chevrons if they don't exist (using folder tab bar approach)
        // Only add chevrons on desktop to prevent mobile touch interference
        if (toolbarContainer && !toolbarContainer.querySelector('button[aria-label="Scroll left"]') && window.innerWidth >= 768) {
          console.log('Adding scroll chevrons (desktop only)')
          
          // Create a wrapper div for the toolbar (like folder tab bar)
          const toolbarWrapper = document.createElement('div')
          toolbarWrapper.className = 'relative w-full max-w-full overflow-x-hidden'
          
          // Create left chevron
          const leftChevron = document.createElement('button')
          leftChevron.type = 'button'
          leftChevron.setAttribute('aria-label', 'Scroll left')
          leftChevron.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15,18 9,12 15,6"></polyline></svg>'
          
          // Create right chevron
          const rightChevron = document.createElement('button')
          rightChevron.type = 'button'
          rightChevron.setAttribute('aria-label', 'Scroll right')
          rightChevron.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9,18 15,12 9,6"></polyline></svg>'
          
          // Add scroll functionality (same as folder tab bar)
          const scrollToolbar = (direction) => {
            const scrollAmount = 200
            const newScrollLeft = direction === 'left' 
              ? toolbarContainer.scrollLeft - scrollAmount
              : toolbarContainer.scrollLeft + scrollAmount

            toolbarContainer.scrollTo({
              left: newScrollLeft,
              behavior: 'smooth'
            })
          }
          
          leftChevron.addEventListener('click', (e) => {
            e.preventDefault()
            scrollToolbar('left')
          })
          
          rightChevron.addEventListener('click', (e) => {
            e.preventDefault()
            scrollToolbar('right')
          })
          
          // Wrap the toolbar and add chevrons
          const toolbarParent = toolbarContainer.parentElement
          if (toolbarParent) {
            // Insert wrapper before toolbar
            toolbarParent.insertBefore(toolbarWrapper, toolbarContainer)
            // Move toolbar into wrapper
            toolbarWrapper.appendChild(toolbarContainer)
            // Add chevrons to wrapper (outside scrollable area)
            toolbarWrapper.appendChild(leftChevron)
            toolbarWrapper.appendChild(rightChevron)
            console.log('Scroll chevrons added with wrapper structure')
          } else {
            // Fallback: add chevrons to toolbar container
            toolbarContainer.appendChild(leftChevron)
            toolbarContainer.appendChild(rightChevron)
            console.log('Scroll chevrons added to toolbar container (fallback)')
          }
        } else if (window.innerWidth < 768) {
          console.log('Skipping chevron creation on mobile to prevent touch interference')
        }

        // Create attachment button if not exists
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-attachment')) {
          const attachmentButton = document.createElement('button')
          attachmentButton.type = 'button'
          attachmentButton.className = 'ql-attachment'
          attachmentButton.title = 'Attach File'
          attachmentButton.innerHTML = '<svg viewBox="0 0 18 18"><path d="M12.5 3.5L6 10c-.8.8-.8 2.1 0 2.9s2.1.8 2.9 0l6.5-6.5c1.4-1.4 1.4-3.7 0-5.1s-3.7-1.4-5.1 0L3.8 7.8c-2 2-2 5.2 0 7.2s5.2 2 7.2 0L16.5 9.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          
          attachmentButton.addEventListener('click', (e) => {
            e.preventDefault()
            const input = document.createElement('input')
            input.setAttribute('type', 'file')
            input.setAttribute('accept', '*/*')
            input.style.display = 'none'
            
            input.addEventListener('change', (e) => {
              const file = e.target.files?.[0]
              if (file) {
                handleFileAttachment(file)
              }
              document.body.removeChild(input)
            })
            
            document.body.appendChild(input)
            input.click()
          })
          
          console.log('Adding attachment button to toolbar')
          // Insert after emoji button if exists, or after image button
          const emojiButton = toolbarContainer.querySelector('.ql-emoji')
          const imageButton = toolbarContainer.querySelector('.ql-image')
          if (emojiButton && emojiButton.parentNode) {
            emojiButton.parentNode.insertBefore(attachmentButton, emojiButton.nextSibling)
            console.log('Attachment button inserted after emoji button')
          } else if (imageButton && imageButton.parentNode) {
            imageButton.parentNode.insertBefore(attachmentButton, imageButton.nextSibling)
            console.log('Attachment button inserted after image button')
          } else {
            const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
            if (toolbarGroup) {
              toolbarGroup.appendChild(attachmentButton)
              console.log('Attachment button appended to toolbar directly')
            }
          }
        }

        // Create table button for quill-table-up
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-table')) {
          console.log('Creating table button...')
          const tableButton = document.createElement('button')
          tableButton.type = 'button'
          tableButton.className = 'ql-table'
          tableButton.title = 'Insert Table'
          // Table icon SVG
          tableButton.innerHTML = `<svg viewBox="0 0 24 24" style="width: 18px; height: 18px; display: block;">
            <path fill="currentColor" d="M3 3h18v18H3V3zm2 2v4h4V5H5zm6 0v4h4V5h-4zm6 0v4h4V5h-4zM5 11v4h4v-4H5zm6 0v4h4v-4h-4zm6 0v4h4v-4h-4zM5 17v2h4v-2H5zm6 0v2h4v-2h-4zm6 0v2h4v-2h-4z"/>
          </svg>`
          
          tableButton.addEventListener('click', (e) => {
            e.preventDefault()
            console.log('Table button clicked')
            
            const editor = quillRef.current?.getEditor()
            if (editor) {
              // Insert a simple table using our custom blot
              const range = editor.getSelection(true)
              if (range) {
                editor.insertEmbed(range.index, 'table', { rows: 3, cols: 3 })
                
                // Ensure there's a paragraph after the table for text input
                setTimeout(() => {
                  const tables = editor.root.querySelectorAll('table.ql-table')
                  tables.forEach(table => {
                    const nextSibling = table.nextSibling
                    if (!nextSibling || nextSibling.nodeType !== Node.ELEMENT_NODE || nextSibling.tagName !== 'P') {
                      const paragraph = document.createElement('p')
                      paragraph.innerHTML = '<br>'
                      table.parentNode.insertBefore(paragraph, table.nextSibling)
                      console.log('🔧 Added paragraph after new table for text input')
                    }
                  })
                }, 100)
                editor.setSelection(range.index + 1)
                console.log('Table inserted successfully')
              }
            }
          })
          
          console.log('Adding table button to toolbar')
          // Insert after link button
          const linkButton = toolbarContainer.querySelector('.ql-link')
          if (linkButton && linkButton.parentNode) {
            linkButton.parentNode.insertBefore(tableButton, linkButton.nextSibling)
            console.log('Table button inserted after link button')
          } else {
            const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
            if (toolbarGroup) {
              toolbarGroup.appendChild(tableButton)
              console.log('Table button appended to toolbar')
            }
          }
        }

        // Create AI search button if not exists
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-ai-search')) {
          console.log('Creating AI search button...')
          const aiButton = document.createElement('button')
          aiButton.type = 'button'
          aiButton.className = 'ql-ai-search'
          aiButton.title = 'AI Search'
          // AI icon using desktop-matching PNG
          aiButton.innerHTML = `<img src="${aiIcon}" alt="AI" style="width: 18px; height: 18px; display: block;" />`
          
          aiButton.addEventListener('click', (e) => {
            e.preventDefault()
            console.log('AI button clicked!')
            handleOpenAIModal()
          })
          
          console.log('Adding AI search button to toolbar')
          // Insert after attachment button if exists
          const attachmentButton = toolbarContainer.querySelector('.ql-attachment')
          if (attachmentButton && attachmentButton.parentNode) {
            attachmentButton.parentNode.insertBefore(aiButton, attachmentButton.nextSibling)
            console.log('AI button inserted after attachment button')
          } else {
            const emojiButton = toolbarContainer.querySelector('.ql-emoji')
            const imageButton = toolbarContainer.querySelector('.ql-image')
            if (emojiButton && emojiButton.parentNode) {
              emojiButton.parentNode.insertBefore(aiButton, emojiButton.nextSibling)
              console.log('AI button inserted after emoji button')
            } else if (imageButton && imageButton.parentNode) {
              imageButton.parentNode.insertBefore(aiButton, imageButton.nextSibling)
              console.log('AI button inserted after image button')
            } else {
              const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
              if (toolbarGroup) {
                toolbarGroup.appendChild(aiButton)
                console.log('AI button appended to toolbar directly')
              }
            }
          }
          console.log('AI button created and added successfully')
        } else {
          console.log('AI button already exists or toolbar not found')
        }

        // Create custom color picker buttons
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-color-picker-custom')) {
          console.log('Creating custom color picker buttons...')
          
          // Text color picker button
          const textColorButton = document.createElement('button')
          textColorButton.type = 'button'
          textColorButton.className = 'ql-color-picker-custom'
          textColorButton.title = 'Text Color'
          textColorButton.innerHTML = `<svg width="18" height="18" viewBox="0 0 1920 1920"><path fill="currentColor" d="M1846.308 1476.923V1920H74v-443.077h1772.308Zm-147.693 147.692H221.692v147.693h1476.923v-147.693ZM1109.751.06l509.391 1227.028-136.468 56.566-164.972-397.588H602.576l-164.972 397.588-136.468-56.566L810.526.059h299.225Zm-98.658 147.692h-101.76L663.868 738.373h592.542L1011.093 147.75Z" fill-rule="evenodd"/></svg>`
          
          textColorButton.addEventListener('click', (e) => {
            e.preventDefault()
            console.log('Text color picker clicked!')
            handleOpenColorPicker('text')
          })
          
          // Background color picker button
          const bgColorButton = document.createElement('button')
          bgColorButton.type = 'button'
          bgColorButton.className = 'ql-background-picker-custom'
          bgColorButton.title = 'Background Color'
          bgColorButton.innerHTML = `<svg width="18" height="18" viewBox="0 0 48 48"><path fill="currentColor" d="M43,3H5A2,2,0,0,0,3,5V43a2,2,0,0,0,2,2H43a2,2,0,0,0,2-2V5A2,2,0,0,0,43,3ZM33.4,37l-2.6-6H17.2l-2.6,6H10.2L21.5,11h5L37.8,37ZM18.9,27H29.1L24,14.9Z"/></svg>`
          
          bgColorButton.addEventListener('click', (e) => {
            e.preventDefault()
            console.log('Background color picker clicked!')
            handleOpenColorPicker('background')
          })
          
          console.log('Adding custom color picker buttons to toolbar')
          // Find the color picker group or create one
          let colorGroup = toolbarContainer.querySelector('.ql-formats:nth-child(4)') // 4th group contains colors
          if (!colorGroup) {
            // Create a new formats group for color pickers
            colorGroup = document.createElement('span')
            colorGroup.className = 'ql-formats'
            toolbarContainer.appendChild(colorGroup)
          }
          
          colorGroup.appendChild(textColorButton)
          colorGroup.appendChild(bgColorButton)
          console.log('Custom color picker buttons created and added successfully')
        } else {
          console.log('Custom color picker buttons already exist or toolbar not found')
        }

        // Create custom picker buttons (header and align)
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-header-picker-custom')) {
          console.log('Creating custom picker buttons...')
          
          // Header picker button
          const headerButton = document.createElement('button')
          headerButton.type = 'button'
          headerButton.className = 'ql-header-picker-custom'
          headerButton.title = 'Heading Style'
          headerButton.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24"><path d="M7 5V12M7 12V19M7 12H17M17 5V12M17 12V19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`
          
          headerButton.addEventListener('click', (e) => {
            e.preventDefault()
            console.log('Header picker clicked!')
            handleOpenPicker('header')
          })
          
          // Align picker button
          const alignButton = document.createElement('button')
          alignButton.type = 'button'
          alignButton.className = 'ql-align-picker-custom'
          alignButton.title = 'Text Alignment'
          alignButton.innerHTML = `<svg width="18" height="18" viewBox="0 0 18 18"><path fill="currentColor" d="M2 4h14v2H2zm0 4h14v2H2zm0 4h10v2H2z"/></svg>`
          
          alignButton.addEventListener('click', (e) => {
            e.preventDefault()
            console.log('Align picker clicked!')
            handleOpenPicker('align')
          })
          
          console.log('Adding custom picker buttons to toolbar')
          // Add header button to first group
          const headerGroup = toolbarContainer.querySelector('.ql-formats:first-child')
          if (headerGroup) {
            headerGroup.appendChild(headerButton)
          }
          
          // Add align button to align group (5th group)
          const alignGroup = toolbarContainer.querySelector('.ql-formats:nth-child(5)')
          if (alignGroup) {
            alignGroup.appendChild(alignButton)
          } else {
            // Create align group if it doesn't exist
            const newAlignGroup = document.createElement('span')
            newAlignGroup.className = 'ql-formats'
            newAlignGroup.appendChild(alignButton)
            toolbarContainer.appendChild(newAlignGroup)
          }
          
          console.log('Custom picker buttons created and added successfully')
        } else {
          console.log('Custom picker buttons already exist or toolbar not found')
        }

        // DEBUG: Analyze all toolbar buttons to find the root cause of blank spaces
        if (toolbarContainer) {
          console.log('🔍 DEBUGGING: Analyzing all toolbar buttons...')
          
          const allButtons = toolbarContainer.querySelectorAll('button')
          const allGroups = toolbarContainer.querySelectorAll('.ql-formats')
          
          console.log(`📊 Found ${allButtons.length} buttons in ${allGroups.length} groups`)
          
          allGroups.forEach((group, groupIndex) => {
            const buttonsInGroup = group.querySelectorAll('button')
            console.log(`📁 Group ${groupIndex + 1}:`, {
              className: group.className,
              buttonCount: buttonsInGroup.length,
              innerHTML: group.innerHTML.length > 100 ? group.innerHTML.substring(0, 100) + '...' : group.innerHTML,
              isEmpty: buttonsInGroup.length === 0
            })
            
            buttonsInGroup.forEach((button, buttonIndex) => {
              console.log(`  🔘 Button ${buttonIndex + 1}:`, {
                className: button.className,
                hasContent: button.innerHTML.trim().length > 0,
                isVisible: button.offsetWidth > 0 && button.offsetHeight > 0,
                dimensions: `${button.offsetWidth}x${button.offsetHeight}`,
                innerHTML: button.innerHTML.substring(0, 50) + (button.innerHTML.length > 50 ? '...' : ''),
                computedDisplay: window.getComputedStyle(button).display,
                computedVisibility: window.getComputedStyle(button).visibility
              })
            })
          })
          
          // Only remove empty groups for now, don't touch buttons
          const emptyGroups = toolbarContainer.querySelectorAll('.ql-formats:empty')
          emptyGroups.forEach((group, index) => {
            console.log(`🗑️ Removing empty group ${index + 1}`)
            group.remove()
          })
          
          console.log('🔍 DEBUG: Analysis complete. Check console for blank button details.')
        }

        // Create drawing canvas button if not exists
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-drawing-canvas')) {
          console.log('Creating drawing canvas button...')
          const drawingButton = document.createElement('button')
          drawingButton.type = 'button'
          drawingButton.className = 'ql-drawing-canvas'
          drawingButton.title = 'Drawing Canvas'
          // Drawing icon - using the new pencil SVG
          drawingButton.innerHTML = `<svg viewBox="0 0 32 32" style="width: 18px; height: 18px; display: block;">
            <path d="M30.133 1.552c-1.090-1.044-2.291-1.573-3.574-1.573-2.006 0-3.47 1.296-3.87 1.693-0.564 0.558-19.786 19.788-19.786 19.788-0.126 0.126-0.217 0.284-0.264 0.456-0.433 1.602-2.605 8.71-2.627 8.782-0.112 0.364-0.012 0.761 0.256 1.029 0.193 0.192 0.45 0.295 0.713 0.295 0.104 0 0.208-0.016 0.31-0.049 0.073-0.024 7.41-2.395 8.618-2.756 0.159-0.048 0.305-0.134 0.423-0.251 0.763-0.754 18.691-18.483 19.881-19.712 1.231-1.268 1.843-2.59 1.819-3.925-0.025-1.319-0.664-2.589-1.901-3.776zM22.37 4.87c0.509 0.123 1.711 0.527 2.938 1.765 1.24 1.251 1.575 2.681 1.638 3.007-3.932 3.912-12.983 12.867-16.551 16.396-0.329-0.767-0.862-1.692-1.719-2.555-1.046-1.054-2.111-1.649-2.932-1.984 3.531-3.532 12.753-12.757 16.625-16.628zM4.387 23.186c0.55 0.146 1.691 0.57 2.854 1.742 0.896 0.904 1.319 1.9 1.509 2.508-1.39 0.447-4.434 1.497-6.367 2.121 0.573-1.886 1.541-4.822 2.004-6.371zM28.763 7.824c-0.041 0.042-0.109 0.11-0.19 0.192-0.316-0.814-0.87-1.86-1.831-2.828-0.981-0.989-1.976-1.572-2.773-1.917 0.068-0.067 0.12-0.12 0.141-0.14 0.114-0.113 1.153-1.106 2.447-1.106 0.745 0 1.477 0.34 2.175 1.010 0.828 0.795 1.256 1.579 1.27 2.331 0.014 0.768-0.404 1.595-1.24 2.458z" fill="currentColor"/>
          </svg>`
          
          drawingButton.addEventListener('click', (e) => {
            e.preventDefault()
            console.log('Drawing button clicked!')
            handleOpenDrawingModal()
          })
          
          console.log('Adding drawing canvas button to toolbar')
          // Insert after AI button if exists
          const aiButton = toolbarContainer.querySelector('.ql-ai-search')
          if (aiButton && aiButton.parentNode) {
            aiButton.parentNode.insertBefore(drawingButton, aiButton.nextSibling)
            console.log('Drawing button inserted after AI button')
          } else {
            // Fallback: insert after attachment button
            const attachmentButton = toolbarContainer.querySelector('.ql-attachment')
            if (attachmentButton && attachmentButton.parentNode) {
              attachmentButton.parentNode.insertBefore(drawingButton, attachmentButton.nextSibling)
              console.log('Drawing button inserted after attachment button')
            } else {
              const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
              if (toolbarGroup) {
                toolbarGroup.appendChild(drawingButton)
                console.log('Drawing button appended to toolbar directly')
              }
            }
          }
          console.log('Drawing button created and added successfully')
        } else {
          console.log('Drawing button already exists or toolbar not found')
        }

        // Create audio recording button if not exists
        if (toolbarContainer && !toolbarContainer.querySelector('.ql-audio-recorder')) {
          console.log('Creating audio recording button...')
          const audioButton = document.createElement('button')
          audioButton.type = 'button'
          audioButton.className = 'ql-audio-recorder'
          audioButton.title = 'Record Audio'
          // Microphone icon SVG
          audioButton.innerHTML = `<svg viewBox="0 0 24 24" style="width: 18px; height: 18px; display: block;">
            <path fill="currentColor" d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
            <path fill="currentColor" d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
          </svg>`
          
          audioButton.addEventListener('click', (e) => {
            e.preventDefault()
            console.log('Audio recording button clicked!')
            handleOpenAudioModal()
          })
          
          console.log('Adding audio recording button to toolbar')
          // Insert after drawing button if exists
          const drawingButton = toolbarContainer.querySelector('.ql-drawing-canvas')
          if (drawingButton && drawingButton.parentNode) {
            drawingButton.parentNode.insertBefore(audioButton, drawingButton.nextSibling)
            console.log('Audio button inserted after drawing button')
          } else {
            // Fallback: insert after AI button
            const aiButton = toolbarContainer.querySelector('.ql-ai-search')
            if (aiButton && aiButton.parentNode) {
              aiButton.parentNode.insertBefore(audioButton, aiButton.nextSibling)
              console.log('Audio button inserted after AI button')
            } else {
              // Final fallback: append to toolbar group
              const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
              if (toolbarGroup) {
                toolbarGroup.appendChild(audioButton)
                console.log('Audio button appended to toolbar directly')
              }
            }
          }
          console.log('Audio recording button created and added successfully')
        } else {
          console.log('Audio recording button already exists or toolbar not found')
        }
      }
    }, 500)

    // Also try with MutationObserver as fallback
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const toolbarContainer = document.querySelector('.ql-toolbar')
          console.log('MutationObserver: toolbar container found:', !!toolbarContainer)
          
          // Create undo button if not exists
          if (toolbarContainer && !toolbarContainer.querySelector('.ql-undo')) {
            console.log('Adding custom undo button via MutationObserver')
            
            const undoButton = document.createElement('button')
            undoButton.type = 'button'
            undoButton.className = 'ql-undo'
            undoButton.title = 'Undo'
            undoButton.innerHTML = '<svg viewBox="0 0 18 18"><path d="M4 7.5L1.5 5L4 2.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.5 5h8.5c3 0 5.5 2.5 5.5 5.5s-2.5 5.5-5.5 5.5h-2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
            
            undoButton.addEventListener('click', (e) => {
              e.preventDefault()
              const quill = quillRef.current?.getEditor()
              if (quill) {
                quill.history.undo()
              }
            })
            
            // Insert at the beginning of the first toolbar group
            const firstToolbarGroup = toolbarContainer.querySelector('span.ql-formats')
            if (firstToolbarGroup) {
              firstToolbarGroup.insertBefore(undoButton, firstToolbarGroup.firstChild)
              console.log('Undo button inserted via MutationObserver at beginning of toolbar')
            }
          }

          // Create redo button if not exists
          if (toolbarContainer && !toolbarContainer.querySelector('.ql-redo')) {
            console.log('Adding custom redo button via MutationObserver')
            
            const redoButton = document.createElement('button')
            redoButton.type = 'button'
            redoButton.className = 'ql-redo'
            redoButton.title = 'Redo'
            redoButton.innerHTML = '<svg viewBox="0 0 18 18"><path d="M14 7.5L16.5 5L14 2.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.5 5H8c-3 0-5.5 2.5-5.5 5.5S5 16 8 16h2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
            
            redoButton.addEventListener('click', (e) => {
              e.preventDefault()
              const quill = quillRef.current?.getEditor()
              if (quill) {
                quill.history.redo()
              }
            })
            
            // Insert after undo button
            const undoButton = toolbarContainer.querySelector('.ql-undo')
            if (undoButton && undoButton.parentNode) {
              undoButton.parentNode.insertBefore(redoButton, undoButton.nextSibling)
              console.log('Redo button inserted via MutationObserver after undo button')
            }
          }

          if (toolbarContainer && !toolbarContainer.querySelector('.ql-emoji')) {
            console.log('Adding custom emoji button via MutationObserver')
            
            // Create emoji button
            const emojiButton = document.createElement('button')
            emojiButton.type = 'button'
            emojiButton.className = 'ql-emoji'
            emojiButton.title = 'Insert Emoji'
            
            // Add emoji SVG icon
            emojiButton.innerHTML = '<svg viewBox="0 0 18 18"><circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="6.5" cy="7" r="1" fill="currentColor"/><circle cx="11.5" cy="7" r="1" fill="currentColor"/><path d="M6 11.5 Q9 13.5 12 11.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
            
            emojiButton.addEventListener('click', (e) => {
              e.preventDefault()
              setIsEmojiPickerOpen(true)
            })
            
            // Insert after image button or append to first toolbar group
            const imageButton = toolbarContainer.querySelector('.ql-image')
            if (imageButton && imageButton.parentNode) {
              imageButton.parentNode.insertBefore(emojiButton, imageButton.nextSibling)
              console.log('Emoji button inserted via MutationObserver after image button')
            } else {
              const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
              if (toolbarGroup) {
                toolbarGroup.appendChild(emojiButton)
                console.log('Emoji button appended via MutationObserver to toolbar group')
              }
            }
          }

          // Create attachment button if not exists
          if (toolbarContainer && !toolbarContainer.querySelector('.ql-attachment')) {
            const attachmentButton = document.createElement('button')
            attachmentButton.type = 'button'
            attachmentButton.className = 'ql-attachment'
            attachmentButton.title = 'Attach File'
            attachmentButton.innerHTML = '<svg viewBox="0 0 18 18"><path d="M12.5 3.5L6 10c-.8.8-.8 2.1 0 2.9s2.1.8 2.9 0l6.5-6.5c1.4-1.4 1.4-3.7 0-5.1s-3.7-1.4-5.1 0L3.8 7.8c-2 2-2 5.2 0 7.2s5.2 2 7.2 0L16.5 9.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            
            attachmentButton.addEventListener('click', (e) => {
              e.preventDefault()
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', '*/*')
              input.style.display = 'none'
              
              input.addEventListener('change', (e) => {
                const file = e.target.files?.[0]
                if (file) {
                  handleFileAttachment(file)
                }
                document.body.removeChild(input)
              })
              
              document.body.appendChild(input)
              input.click()
            })
            
            // Insert after emoji button if it exists, otherwise after image button
            const emojiButton = toolbarContainer.querySelector('.ql-emoji')
            const imageButton = toolbarContainer.querySelector('.ql-image')
            if (emojiButton && emojiButton.parentNode) {
              emojiButton.parentNode.insertBefore(attachmentButton, emojiButton.nextSibling)
              console.log('Attachment button inserted via MutationObserver after emoji button')
            } else if (imageButton && imageButton.parentNode) {
              imageButton.parentNode.insertBefore(attachmentButton, imageButton.nextSibling)
              console.log('Attachment button inserted via MutationObserver after image button')
            } else {
              const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
              if (toolbarGroup) {
                toolbarGroup.appendChild(attachmentButton)
                console.log('Attachment button appended via MutationObserver to toolbar group')
              }
            }
          }

          // Create table button for quill-table-up via MutationObserver
          if (toolbarContainer && !toolbarContainer.querySelector('.ql-table')) {
            console.log('Creating table button via MutationObserver...')
            const tableButton = document.createElement('button')
            tableButton.type = 'button'
            tableButton.className = 'ql-table'
            tableButton.title = 'Insert Table'
            // Table icon SVG
            tableButton.innerHTML = `<svg viewBox="0 0 24 24" style="width: 18px; height: 18px; display: block;">
              <path fill="currentColor" d="M3 3h18v18H3V3zm2 2v4h4V5H5zm6 0v4h4V5h-4zm6 0v4h4V5h-4zM5 11v4h4v-4H5zm6 0v4h4v-4h-4zm6 0v4h4v-4h-4zM5 17v2h4v-2H5zm6 0v2h4v-2h-4zm6 0v2h4v-2h-4z"/>
            </svg>`
            
            tableButton.addEventListener('click', (e) => {
              e.preventDefault()
              console.log('Table button clicked via MutationObserver')
              
              const editor = quillRef.current?.getEditor()
              if (editor) {
                // Insert a simple table using our custom blot
                const range = editor.getSelection(true)
                if (range) {
                  editor.insertEmbed(range.index, 'table', { rows: 3, cols: 3 })
                
                // Ensure there's a paragraph after the table for text input
                setTimeout(() => {
                  const tables = editor.root.querySelectorAll('table.ql-table')
                  tables.forEach(table => {
                    const nextSibling = table.nextSibling
                    if (!nextSibling || nextSibling.nodeType !== Node.ELEMENT_NODE || nextSibling.tagName !== 'P') {
                      const paragraph = document.createElement('p')
                      paragraph.innerHTML = '<br>'
                      table.parentNode.insertBefore(paragraph, table.nextSibling)
                      console.log('🔧 Added paragraph after new table for text input')
                    }
                  })
                }, 100)
                  editor.setSelection(range.index + 1)
                  console.log('Table inserted successfully via MutationObserver')
                }
              }
            })
            
            console.log('Adding table button via MutationObserver to toolbar')
            // Insert after link button
            const linkButton = toolbarContainer.querySelector('.ql-link')
            if (linkButton && linkButton.parentNode) {
              linkButton.parentNode.insertBefore(tableButton, linkButton.nextSibling)
              console.log('Table button inserted via MutationObserver after link button')
            } else {
              const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
              if (toolbarGroup) {
                toolbarGroup.appendChild(tableButton)
                console.log('Table button appended via MutationObserver to toolbar')
              }
            }
          }

          // Create custom image button if not exists
          if (toolbarContainer && !toolbarContainer.querySelector('.ql-image')) {
            console.log('Creating custom image button via MutationObserver...')
            const imageButton = document.createElement('button')
            imageButton.type = 'button'
            imageButton.className = 'ql-image'
            imageButton.innerHTML = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="10" width="12" x="3" y="4"></rect><circle class="ql-fill" cx="6" cy="7" r="1"></circle><polyline class="ql-even ql-fill" points="5,12 5,11 7,9 8,10 11,7 11,12 5,12"></polyline></svg>'
            imageButton.title = 'Insert Image'
            
            // Add explicit styling to ensure visibility
            imageButton.style.cssText = `
              display: inline-block;
              width: 28px;
              height: 28px;
              padding: 4px;
              margin: 0 1px;
              border: 1px solid transparent;
              border-radius: 3px;
              background: transparent;
              cursor: pointer;
              vertical-align: middle;
              color: #444;
            `
            imageButton.style.color = '#444'
            
            // Add hover effects
            imageButton.addEventListener('mouseenter', () => {
              imageButton.style.backgroundColor = '#f0f0f0'
              imageButton.style.borderColor = '#ccc'
            })
            
            imageButton.addEventListener('mouseleave', () => {
              imageButton.style.backgroundColor = 'transparent'
              imageButton.style.borderColor = 'transparent'
            })
            
            imageButton.addEventListener('click', (e) => {
              e.preventDefault()
              console.log('🔍 Custom image button clicked via MutationObserver!')
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', 'image/*')
              input.style.display = 'none'
              
              input.addEventListener('change', async (e) => {
                console.log('🔍 File input change event triggered')
                const file = e.target.files?.[0]
                if (file) {
                  console.log('🔍 File selected, calling handleImageUpload')
                  try {
                    await handleImageUpload(file)
                    console.log('🔍 handleImageUpload completed successfully')
                  } catch (error) {
                    console.error('❌ handleImageUpload failed:', error)
                  }
                } else {
                  console.log('🔍 No file selected')
                }
                document.body.removeChild(input)
              })
              
              document.body.appendChild(input)
              input.click()
            })
            
            // Insert after link button if it exists
            const linkButton = toolbarContainer.querySelector('.ql-link')
            if (linkButton && linkButton.parentNode) {
              linkButton.parentNode.insertBefore(imageButton, linkButton.nextSibling)
              console.log('✅ Custom image button inserted via MutationObserver after link button')
            } else {
              // Fallback: append to the first toolbar group
              const firstGroup = toolbarContainer.querySelector('span.ql-formats')
              if (firstGroup) {
                firstGroup.appendChild(imageButton)
                console.log('✅ Custom image button appended via MutationObserver to first toolbar group')
              } else {
                // Last resort: append directly to toolbar
                toolbarContainer.appendChild(imageButton)
                console.log('✅ Custom image button appended via MutationObserver directly to toolbar container')
              }
            }
            
            // Add some debugging to check if button is visible
            console.log('🔍 Image button added via MutationObserver:', {
              exists: !!imageButton,
              visible: imageButton.offsetWidth > 0,
              parent: imageButton.parentNode?.className,
              position: imageButton.parentNode?.children ? Array.from(imageButton.parentNode.children).indexOf(imageButton) : -1
            })
          }

          // Create AI search button if not exists
          if (toolbarContainer && !toolbarContainer.querySelector('.ql-ai-search')) {
            console.log('Creating AI search button via MutationObserver...')
            const aiButton = document.createElement('button')
            aiButton.type = 'button'
            aiButton.className = 'ql-ai-search'
            aiButton.title = 'AI Search'
            // AI icon using desktop-matching PNG
            aiButton.innerHTML = `<img src="${aiIcon}" alt="AI" style="width: 18px; height: 18px; display: block;" />`
            
            aiButton.addEventListener('click', (e) => {
              e.preventDefault()
              console.log('AI button clicked via MutationObserver!')
              handleOpenAIModal()
            })
            
            // Insert after attachment button if it exists
            const attachmentButton = toolbarContainer.querySelector('.ql-attachment')
            if (attachmentButton && attachmentButton.parentNode) {
              attachmentButton.parentNode.insertBefore(aiButton, attachmentButton.nextSibling)
              console.log('AI button inserted via MutationObserver after attachment button')
            } else {
              const emojiButton = toolbarContainer.querySelector('.ql-emoji')
              const imageButton = toolbarContainer.querySelector('.ql-image')
              if (emojiButton && emojiButton.parentNode) {
                emojiButton.parentNode.insertBefore(aiButton, emojiButton.nextSibling)
                console.log('AI button inserted via MutationObserver after emoji button')
              } else if (imageButton && imageButton.parentNode) {
                imageButton.parentNode.insertBefore(aiButton, imageButton.nextSibling)
                console.log('AI button inserted via MutationObserver after image button')
              } else {
                const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
                if (toolbarGroup) {
                  toolbarGroup.appendChild(aiButton)
                  console.log('AI button appended via MutationObserver to toolbar group')
                }
              }
            }
          }

          // Create drawing canvas button if not exists
          if (toolbarContainer && !toolbarContainer.querySelector('.ql-drawing-canvas')) {
            console.log('Creating drawing canvas button via MutationObserver...')
            const drawingButton = document.createElement('button')
            drawingButton.type = 'button'
            drawingButton.className = 'ql-drawing-canvas'
            drawingButton.title = 'Drawing Canvas'
            // Drawing icon - using the new pencil SVG
            drawingButton.innerHTML = `<svg viewBox="0 0 32 32" style="width: 18px; height: 18px; display: block;">
              <path d="M30.133 1.552c-1.090-1.044-2.291-1.573-3.574-1.573-2.006 0-3.47 1.296-3.87 1.693-0.564 0.558-19.786 19.788-19.786 19.788-0.126 0.126-0.217 0.284-0.264 0.456-0.433 1.602-2.605 8.71-2.627 8.782-0.112 0.364-0.012 0.761 0.256 1.029 0.193 0.192 0.45 0.295 0.713 0.295 0.104 0 0.208-0.016 0.31-0.049 0.073-0.024 7.41-2.395 8.618-2.756 0.159-0.048 0.305-0.134 0.423-0.251 0.763-0.754 18.691-18.483 19.881-19.712 1.231-1.268 1.843-2.59 1.819-3.925-0.025-1.319-0.664-2.589-1.901-3.776zM22.37 4.87c0.509 0.123 1.711 0.527 2.938 1.765 1.24 1.251 1.575 2.681 1.638 3.007-3.932 3.912-12.983 12.867-16.551 16.396-0.329-0.767-0.862-1.692-1.719-2.555-1.046-1.054-2.111-1.649-2.932-1.984 3.531-3.532 12.753-12.757 16.625-16.628zM4.387 23.186c0.55 0.146 1.691 0.57 2.854 1.742 0.896 0.904 1.319 1.9 1.509 2.508-1.39 0.447-4.434 1.497-6.367 2.121 0.573-1.886 1.541-4.822 2.004-6.371zM28.763 7.824c-0.041 0.042-0.109 0.11-0.19 0.192-0.316-0.814-0.87-1.86-1.831-2.828-0.981-0.989-1.976-1.572-2.773-1.917 0.068-0.067 0.12-0.12 0.141-0.14 0.114-0.113 1.153-1.106 2.447-1.106 0.745 0 1.477 0.34 2.175 1.010 0.828 0.795 1.256 1.579 1.27 2.331 0.014 0.768-0.404 1.595-1.24 2.458z" fill="currentColor"/>
            </svg>`
            
            drawingButton.addEventListener('click', (e) => {
              e.preventDefault()
              console.log('Drawing button clicked via MutationObserver!')
              handleOpenDrawingModal()
            })
            
            // Insert after AI button if exists
            const aiButton = toolbarContainer.querySelector('.ql-ai-search')
            if (aiButton && aiButton.parentNode) {
              aiButton.parentNode.insertBefore(drawingButton, aiButton.nextSibling)
              console.log('Drawing button inserted via MutationObserver after AI button')
            } else {
              // Fallback: insert after attachment button
              const attachmentButton = toolbarContainer.querySelector('.ql-attachment')
              if (attachmentButton && attachmentButton.parentNode) {
                attachmentButton.parentNode.insertBefore(drawingButton, attachmentButton.nextSibling)
                console.log('Drawing button inserted via MutationObserver after attachment button')
              } else {
                const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
                if (toolbarGroup) {
                  toolbarGroup.appendChild(drawingButton)
                  console.log('Drawing button appended via MutationObserver to toolbar group')
                }
              }
            }
          }

          // Create audio recording button if not exists
          if (toolbarContainer && !toolbarContainer.querySelector('.ql-audio-recorder')) {
            console.log('Creating audio recording button via MutationObserver...')
            const audioButton = document.createElement('button')
            audioButton.type = 'button'
            audioButton.className = 'ql-audio-recorder'
            audioButton.title = 'Record Audio'
            // Microphone icon SVG
            audioButton.innerHTML = `<svg viewBox="0 0 24 24" style="width: 18px; height: 18px; display: block;">
              <path fill="currentColor" d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
              <path fill="currentColor" d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            </svg>`
            
            audioButton.addEventListener('click', (e) => {
              e.preventDefault()
              console.log('Audio recording button clicked via MutationObserver!')
              handleOpenAudioModal()
            })
            
            // Insert after drawing button if exists
            const drawingButton = toolbarContainer.querySelector('.ql-drawing-canvas')
            if (drawingButton && drawingButton.parentNode) {
              drawingButton.parentNode.insertBefore(audioButton, drawingButton.nextSibling)
              console.log('Audio button inserted via MutationObserver after drawing button')
            } else {
              // Fallback: insert after AI button
              const aiButton = toolbarContainer.querySelector('.ql-ai-search')
              if (aiButton && aiButton.parentNode) {
                aiButton.parentNode.insertBefore(audioButton, aiButton.nextSibling)
                console.log('Audio button inserted via MutationObserver after AI button')
              } else {
                // Final fallback: append to toolbar group
                const toolbarGroup = toolbarContainer.querySelector('span.ql-formats')
                if (toolbarGroup) {
                  toolbarGroup.appendChild(audioButton)
                  console.log('Audio button appended via MutationObserver to toolbar group')
                }
              }
            }
          }
          
          // Stop observing once we've added all buttons
          if (toolbarContainer.querySelector('.ql-undo') && 
              toolbarContainer.querySelector('.ql-redo') &&
              toolbarContainer.querySelector('.ql-image') &&
              toolbarContainer.querySelector('.ql-emoji') && 
              toolbarContainer.querySelector('.ql-attachment') && 
              toolbarContainer.querySelector('.ql-ai-search')) {
            observer.disconnect()
          }
        }
      })
    })
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    return () => {
      clearTimeout(timer)
      observer.disconnect()
      // Clean up paste handler
      if (pasteHandler) {
        const editor = quillRef.current?.getEditor?.()
        if (editor) {
          editor.container.removeEventListener('paste', pasteHandler)
        }
      }
    }
  }, [])

  // Global click handler for attachment links
  useEffect(() => {
    const handleAttachmentClick = (e) => {
      const link = e.target.closest('a')
      if (!link) return
      
      const href = link.getAttribute('href')
      const fileId = link.getAttribute('data-file-id')
      const fileName = link.getAttribute('data-file-name') || link.textContent
      const originalFileName = link.getAttribute('data-original-file-name')
      
      // Check if this is an attachment link that needs handling
      if ((href === 'about:blank' || href === 'javascript:void(0)') && fileName) {
        e.preventDefault()
        e.stopPropagation()
        
        console.log(`🌐 Global handler: Attachment link clicked: ${fileName} (${fileId || 'no-file-id'})`)
        console.log(`🌐 Global handler: Original filename: ${originalFileName}`)
        
        // Handle the download - use the actual filename for searching
        handleAttachmentDownload(fileId, fileName, link)
        return false
      }
    }

    document.addEventListener('click', handleAttachmentClick, true) // Use capture phase
    return () => document.removeEventListener('click', handleAttachmentClick, true)
  }, [])

  // Helper function to handle attachment downloads
  const handleAttachmentDownload = async (fileId, fileName, linkElement) => {
    let spinnerToast = null
    try {
      // Show floating spinner for file operations
      spinnerToast = toast.loading(
        `Opening ${fileName}...`, 
        {
          duration: Infinity, // Don't auto-dismiss
          style: {
            background: 'rgba(31, 41, 55, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }
      )
      
      // Check if this is a local file
      const isLocalFile = linkElement.getAttribute('data-local-file') === 'true' || 
                         linkElement.getAttribute('data-download-url')?.startsWith('local://')
      
      if (isLocalFile && isLocalMode) {
        // Handle local file download
        console.log('🌐 Global handler: Downloading local file:', fileId)
        
        if (!getLocalFile) {
          throw new Error('Local file operations not available')
        }
        
            const localFile = await getLocalFile(fileId)
            const url = window.URL.createObjectURL(localFile.blob)
            
            // Handle MIME types for in-browser opening vs downloading
            const mimeType = localFile.fileType || localFile.mimeType
            
            // Files that should open in browser
            const browserOpenableTypes = [
              'text/plain',
              'text/markdown', 
              'text/html',
              'text/css',
              'text/javascript',
              'application/json',
              'application/pdf',
              'image/jpeg',
              'image/png',
              'image/gif',
              'image/webp',
              'image/svg+xml',
              'video/mp4',
              'video/webm',
              'audio/mp3',
              'audio/wav',
              'audio/webm',
              'audio/ogg',
              'audio/mpeg',
              'application/xml',
              'text/csv',
              'text/xml',
              'application/javascript',
              'text/javascript'
            ]
            
            console.log('🌐 Global handler: Local file MIME type:', mimeType, 'for file:', localFile.fileName)
            
            // Check if MIME type is browser-openable (handle codec specifications like "audio/webm;codecs=opus")
            const baseMimeType = mimeType ? mimeType.split(';')[0].trim() : ''
            const isBrowserOpenable = browserOpenableTypes.includes(mimeType) || 
                                     browserOpenableTypes.includes(baseMimeType) ||
                                     (baseMimeType.startsWith('audio/') && browserOpenableTypes.some(type => type.startsWith('audio/')))
            
            console.log('🌐 Global handler: MIME type check:', { mimeType, baseMimeType, isBrowserOpenable })
            
            if (isBrowserOpenable) {
              // Open in new tab
              console.log('🌐 Global handler: Opening local file in browser:', localFile.fileName)
              const newTab = window.open(url, '_blank')
              if (!newTab) {
                // Fallback if popup blocked
                console.log('🌐 Global handler: Popup blocked, using fallback method')
                const a = document.createElement('a')
                a.href = url
                a.target = '_blank'
                a.click()
              }
            } else {
              // Force download for other file types
              console.log('🌐 Global handler: Downloading local file:', localFile.fileName)
              const a = document.createElement('a')
              a.href = url
              a.download = localFile.fileName
              a.style.display = 'none'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }
            
            // Clean up after a delay
            setTimeout(() => {
              window.URL.revokeObjectURL(url)
            }, 1000)
            
            // Dismiss spinner and show success
            toast.dismiss(spinnerToast)
            toast.success(`File "${localFile.fileName}" opened successfully!`, {
              duration: 2000,
              style: {
                background: 'rgba(16, 185, 129, 0.5)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: 'white',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }
            })
        
      } else {
        // Handle cloud file download
        console.log('🌐 Global handler: Downloading cloud file:', fileId || 'searching by name')
        
        let actualFileId = fileId
        
        // If no file ID or it's a temp ID, search for the file by name
        if (!fileId || fileId.startsWith('temp-')) {
          console.log('🌐 Global handler: Searching for file by name in Google Drive:', fileName)
          
          if (!window.googleDriveAPI) {
            throw new Error('Google Drive API not available')
          }
          
          try {
            // Try searching for the file by name in Google Drive
            const searchQuery = `name='${fileName}' and trashed=false`
            console.log('🌐 Global handler: Search query:', searchQuery)
            
            const searchResponse = await window.googleDriveAPI.apiRequest(`${window.googleDriveAPI.API_BASE}/files`, {
              q: searchQuery,
              fields: 'files(id,name,mimeType)'
            })
            
            console.log('🌐 Global handler: Search response:', searchResponse)
            
            // Parse the response if it's a Response object
            const searchData = searchResponse.files ? searchResponse : await searchResponse.json()
            console.log('🌐 Global handler: Parsed search data:', searchData)
            
            if (searchData.files && searchData.files.length > 0) {
              // Use the first matching file
              actualFileId = searchData.files[0].id
              console.log('🌐 Global handler: Found file in Google Drive:', actualFileId)
            } else {
              throw new Error(`File not found in Google Drive: ${fileName}`)
            }
          } catch (searchError) {
            console.log('🌐 Global handler: Search failed, trying alternative approach:', searchError)
            
            // Fallback: Get recent files and search through them
            const recentFiles = await window.googleDriveAPI.apiRequest(`${window.googleDriveAPI.API_BASE}/files`, {
              q: 'trashed=false',
              fields: 'files(id,name,mimeType)',
              orderBy: 'modifiedTime desc',
              pageSize: 100
            })
            
            console.log('🌐 Global handler: Recent files:', recentFiles)
            
            // Parse the response if it's a Response object
            const recentData = recentFiles.files ? recentFiles : await recentFiles.json()
            console.log('🌐 Global handler: Parsed recent files data:', recentData)
            
            // Log some recent files for debugging
            console.log('🌐 Global handler: Recent files sample:', recentData.files?.slice(0, 5).map(f => f.name))
            
            // Try exact match first (this should work for new uploads with stored exact filename)
            let matchingFile = recentData.files?.find(file => file.name === fileName)
            
            if (matchingFile) {
              console.log('🌐 Global handler: Found exact filename match:', matchingFile.name)
            } else {
              // If no exact match, try case-insensitive match
              console.log('🌐 Global handler: No exact match, trying case-insensitive search')
              matchingFile = recentData.files?.find(file => file.name.toLowerCase() === fileName.toLowerCase())
              
              if (matchingFile) {
                console.log('🌐 Global handler: Found case-insensitive match:', matchingFile.name)
              }
            }
            
  // If still no match, try partial match
  if (!matchingFile) {
    console.log('🌐 Global handler: No case-insensitive match, trying partial search')
    const fileNameWithoutExt = fileName.split('.')[0]
    matchingFile = recentData.files?.find(file =>
      file.name.toLowerCase().includes(fileNameWithoutExt.toLowerCase()) ||
      fileNameWithoutExt.toLowerCase().includes(file.name.split('.')[0].toLowerCase())
    )
  }

  // If still no match, try searching for files that start with the base filename (for timestamped files)
  if (!matchingFile) {
    console.log('🌐 Global handler: No partial match, trying timestamped file search')
    const baseFileName = fileName.replace(/\.[^/.]+$/, '') // Remove extension
    const timestampedFiles = recentData.files?.filter(file => {
      const fileBaseName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
      return fileBaseName.toLowerCase().startsWith(baseFileName.toLowerCase() + '_')
    })
    
    if (timestampedFiles && timestampedFiles.length > 0) {
      console.log(`🌐 Global handler: Found ${timestampedFiles.length} timestamped files with base name '${baseFileName}':`, timestampedFiles.map(f => f.name))
      
      // If multiple files found, get the most recent one (files are already sorted by modifiedTime desc)
      matchingFile = timestampedFiles[0]
      console.log(`🌐 Global handler: Selected most recent file: ${matchingFile.name}`)
      
      // Warn user if multiple files exist with same base name
      if (timestampedFiles.length > 1) {
        console.warn(`⚠️ Global handler: Multiple files found with base name '${baseFileName}'. Selected the most recent one: ${matchingFile.name}`)
        console.warn(`⚠️ Available files: ${timestampedFiles.map(f => f.name).join(', ')}`)
      }
    }
  }

  // If still no match, try searching for files with old random format (timestamp_random.ext)
  if (!matchingFile) {
    console.log('🌐 Global handler: No timestamped match, trying old random format search')
    const baseFileName = fileName.replace(/\.[^/.]+$/, '') // Remove extension
    const oldFormatFiles = recentData.files?.filter(file => {
      // Check if file matches old format: timestamp_random.ext
      const fileBaseName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
      const parts = fileBaseName.split('_')
      if (parts.length === 2) {
        // Check if first part is a timestamp (all digits) and second part is random string
        const isTimestamp = /^\d+$/.test(parts[0])
        const isRandomString = /^[a-z0-9]+$/i.test(parts[1]) && parts[1].length >= 8
        return isTimestamp && isRandomString
      }
      return false
    })
    
    if (oldFormatFiles && oldFormatFiles.length > 0) {
      console.log(`🌐 Global handler: Found ${oldFormatFiles.length} files with old random format:`, oldFormatFiles.map(f => f.name))
      
      // For old format files, we can't match by name, so we'll take the most recent one
      // This is a fallback for files uploaded before the new naming scheme
      matchingFile = oldFormatFiles[0]
      console.log(`🌐 Global handler: Selected most recent old format file: ${matchingFile.name}`)
      console.warn(`⚠️ Global handler: Using old format file as fallback. Consider re-uploading for better naming.`)
    }
  }
            
            if (matchingFile) {
              actualFileId = matchingFile.id
              console.log('🌐 Global handler: Found file in recent files:', actualFileId, matchingFile.name)
            } else {
              console.log('🌐 Global handler: Available files:', recentData.files?.map(f => f.name).slice(0, 10))
              throw new Error(`File not found: ${fileName}`)
            }
          }
        }
        
        // Download the file using Google Drive API
        const downloadResponse = await window.googleDriveAPI.downloadAttachment(actualFileId)
        
        if (downloadResponse && downloadResponse.blob) {
          console.log('🌐 Global handler: Download response:', downloadResponse)
          
          // Handle MIME types for in-browser opening vs downloading
          const url = window.URL.createObjectURL(downloadResponse.blob)
          let mimeType = downloadResponse.fileType || downloadResponse.mimeType
          const fileName = downloadResponse.fileName || fileName
          
          // Fallback MIME type detection from file extension
          if (!mimeType && fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            const mimeTypes = {
              'md': 'text/markdown',
              'txt': 'text/plain',
              'html': 'text/html',
              'htm': 'text/html',
              'css': 'text/css',
              'js': 'text/javascript',
              'json': 'application/json',
              'pdf': 'application/pdf',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'png': 'image/png',
              'gif': 'image/gif',
              'webp': 'image/webp',
              'svg': 'image/svg+xml',
              'mp4': 'video/mp4',
              'webm': 'video/webm',
              'mp3': 'audio/mp3',
              'wav': 'audio/wav',
              'ogg': 'audio/ogg',
              'mpeg': 'audio/mpeg',
              'xml': 'application/xml',
              'csv': 'text/csv'
            }
            mimeType = mimeTypes[ext] || 'application/octet-stream'
            console.log('🌐 Global handler: Detected MIME type from extension:', mimeType, 'for extension:', ext)
          }
          
          // Files that should open in browser
          const browserOpenableTypes = [
            'text/plain',
            'text/markdown', 
            'text/html',
            'text/css',
            'text/javascript',
            'application/json',
            'application/pdf',
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/svg+xml',
            'video/mp4',
            'video/webm',
            'audio/mp3',
            'audio/wav',
            'audio/webm',
            'audio/ogg',
            'audio/mpeg',
            'application/xml',
            'text/csv',
            'text/xml',
            'application/javascript',
            'text/javascript'
          ]
          
          console.log('🌐 Global handler: File MIME type:', mimeType, 'for file:', fileName)
          
          // Check if MIME type is browser-openable (handle codec specifications like "audio/webm;codecs=opus")
          const baseMimeType = mimeType ? mimeType.split(';')[0].trim() : ''
          const isBrowserOpenable = browserOpenableTypes.includes(mimeType) || 
                                   browserOpenableTypes.includes(baseMimeType) ||
                                   (baseMimeType.startsWith('audio/') && browserOpenableTypes.some(type => type.startsWith('audio/')))
          
          console.log('🌐 Global handler: MIME type check:', { mimeType, baseMimeType, isBrowserOpenable })
          
          if (isBrowserOpenable) {
            // Open in new tab
            console.log('🌐 Global handler: Opening file in browser:', fileName)
            const newTab = window.open(url, '_blank')
            if (!newTab) {
              // Fallback if popup blocked
              console.log('🌐 Global handler: Popup blocked, using fallback method')
              const a = document.createElement('a')
              a.href = url
              a.target = '_blank'
              a.click()
            }
          } else {
            // Force download for other file types
            console.log('🌐 Global handler: Downloading file:', fileName)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
          
          // Clean up after a delay
          setTimeout(() => {
            window.URL.revokeObjectURL(url)
          }, 1000)
          
          // Dismiss spinner and show success
          toast.dismiss(spinnerToast)
          toast.success(`File "${fileName}" opened successfully!`, {
            duration: 2000,
            style: {
              background: 'rgba(16, 185, 129, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }
          })
          console.log('🌐 Global handler: File processed successfully:', fileName)
        } else {
          throw new Error('Failed to download file from Google Drive')
        }
      }
    } catch (error) {
      // Dismiss spinner and show error
      if (spinnerToast) {
        toast.dismiss(spinnerToast)
      }
      console.error('🌐 Global handler: Download failed:', error)
      toast.error(`Failed to download file: ${error.message}`, {
        duration: 5000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
    }
  }

  // Process existing attachment links when content changes - DISABLED FOR PERFORMANCE
  useEffect(() => {
    // PERFORMANCE FIX: Disable attachment link processing to prevent lag during typing
    // This was causing repeated DOM queries on every keystroke
    console.log('🔍 RichTextEditor: Attachment link processing DISABLED for performance')
    
    // Only process on initial load, not on every content change
    // updateOldAttachmentLinks() // DISABLED
    // processExistingAttachmentLinks() // DISABLED
  }, []) // Empty dependency array - only run once

  // Also run link processing when the editor is ready
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🔧 Running delayed link processing...')
      updateOldAttachmentLinks()
      processExistingAttachmentLinks()
      
      // Initialize cursor position when editor is ready
    }, 1000) // Run after 1 second to ensure editor is fully loaded

    // Also run immediately for faster response
    updateOldAttachmentLinks()
    processExistingAttachmentLinks()

    return () => clearTimeout(timer)
  }, [])

  // Handle external value changes (when form is reset)
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (quill && value !== undefined) {
      const newHtml = value || ''
      
      // Only update if this is a new value we haven't set before
      if (lastSetValue.current !== newHtml) {
        console.log('RichTextEditor: Updating content from external value change')
        
        // Process image references asynchronously to resolve them to actual images
        processImageReferencesAsync(newHtml).then(processedHtml => {
          // Use dangerouslyPasteHTML to avoid Quill's image loading during conversion
          try {
            // Clear the editor first
            quill.setContents([], 'silent')
            
            // Process table HTML before inserting
            const processedContent = processTableHTML(processedHtml)
            console.log('🔧 RichTextEditor: Processed content for tables:', processedContent.substring(0, 200) + '...')
            
            // Debug: Check what's in the table cells
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = processedContent
            const tables = tempDiv.querySelectorAll('table')
            tables.forEach((table, index) => {
              const cells = table.querySelectorAll('td')
              console.log(`🔧 Table ${index} has ${cells.length} cells:`)
              cells.forEach((cell, cellIndex) => {
                console.log(`  Cell ${cellIndex}: "${cell.innerHTML}"`)
              })
            })
            
            // Use dangerouslyPasteHTML to insert the processed content
            quill.clipboard.dangerouslyPasteHTML(processedContent, 'silent')
            
            // After Quill processes the content, manually set data attributes AND size attributes on img elements
            // This is necessary because dangerouslyPasteHTML doesn't preserve data attributes or custom attributes
            setTimeout(() => {
              console.log('🔧 RichTextEditor: Before fixTableContent, tables found:', quill.root.querySelectorAll('table').length)
              
              // Debug: Check what's in the table cells after Quill processing
              const tablesAfterQuill = quill.root.querySelectorAll('table')
              tablesAfterQuill.forEach((table, index) => {
                const cells = table.querySelectorAll('td')
                console.log(`🔧 After Quill - Table ${index} has ${cells.length} cells:`)
                cells.forEach((cell, cellIndex) => {
                  console.log(`  Cell ${cellIndex}: "${cell.innerHTML}"`)
                })
              })
              
              // Aggressive content restoration before fixTableContent using stored arrays
              const tablesBeforeFix = quill.root.querySelectorAll('table')
              console.log(`🔧 Attempting restoration with processing ID: ${currentProcessingId}`)
              
              tablesBeforeFix.forEach((table, tableIndex) => {
              const storeKey = `${currentProcessingId}-table-${tableIndex}`
              const storedCellContents = tableContentStore.get(storeKey)
              
              // Check if we have stored content for this table
                
                if (storedCellContents) {
                  const cells = table.querySelectorAll('td')
                  cells.forEach((cell, cellIndex) => {
                    const storedContent = storedCellContents[cellIndex]
                    
                    // If cell appears empty or contains only <br> but we have stored content, restore it
                    const isEmpty = !cell.innerHTML || cell.innerHTML.trim() === ''
                    const isBr = cell.innerHTML === '<br>' || cell.innerHTML.trim() === '<br>'
                    const hasStoredContent = storedContent && storedContent.trim() !== '' && storedContent !== '<br>'
                    
                    if ((isEmpty || isBr) && hasStoredContent) {
                      cell.innerHTML = storedContent
                    }
                  })
                }
              })
              
              // Fix table content first
              fixTableContent(quill)
              
              // Post-fix content restoration using stored arrays
              const tablesAfterFix = quill.root.querySelectorAll('table')
              tablesAfterFix.forEach((table, tableIndex) => {
                const storeKey = `${currentProcessingId}-table-${tableIndex}`
                const storedCellContents = tableContentStore.get(storeKey)
                
                if (storedCellContents) {
                  const cells = table.querySelectorAll('td')
                  cells.forEach((cell, cellIndex) => {
                    const storedContent = storedCellContents[cellIndex]
                    
                    // If cell appears empty or contains only <br> but we have stored content, restore it
                    if ((!cell.innerHTML || cell.innerHTML.trim() === '' || cell.innerHTML === '<br>' || cell.innerHTML.trim() === '<br>') && 
                        storedContent && storedContent.trim() !== '' && storedContent !== '<br>') {
                      console.log(`🔧 Post-fix restoration for cell ${cellIndex}: "${storedContent}" (was: "${cell.innerHTML}")`)
                      cell.innerHTML = storedContent
                    }
                  })
                }
              })
              
              // Add event listeners to tables
              addTableEventListeners(quill)
              
              // Ensure table content is visible (fallback for Quill issues) using stored arrays
              const tablesAfterProcessing = quill.root.querySelectorAll('table')
              tablesAfterProcessing.forEach((table, tableIndex) => {
                const storeKey = `${currentProcessingId}-table-${tableIndex}`
                const storedCellContents = tableContentStore.get(storeKey)
                
                const cells = table.querySelectorAll('td')
                cells.forEach((cell, cellIndex) => {
                  // Try to restore from stored content if available
                  if (storedCellContents) {
                    const storedContent = storedCellContents[cellIndex]
                    
                    // If cell appears empty or contains only <br> but we have stored content, restore it
                    if ((!cell.innerHTML || cell.innerHTML.trim() === '' || cell.innerHTML === '<br>' || cell.innerHTML.trim() === '<br>') && 
                        storedContent && storedContent.trim() !== '' && storedContent !== '<br>') {
                      console.log(`🔧 Final fallback restoration for cell ${cellIndex}: "${storedContent}" (was: "${cell.innerHTML}")`)
                      cell.innerHTML = storedContent
                    }
                  }
                  
                  // Ensure cell is visible
                  cell.style.display = 'table-cell'
                  cell.style.visibility = 'visible'
                })
              })
              
              // Don't reset processing ID here - it's still needed for subsequent processing cycles
              console.log(`🔧 Completed processing cycle, keeping processing ID: ${currentProcessingId}`)
              
              
              // Reset processing ID after a delay to allow for all processing cycles to complete
              setTimeout(() => {
                if (currentProcessingId) {
                  console.log(`🔧 Resetting processing ID after delay: ${currentProcessingId}`)
                  currentProcessingId = null
                }
              }, 2000) // 2 second delay to ensure all processing cycles complete
              
              // Set up MutationObserver to watch for table changes
              const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                  if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                      if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.tagName === 'TABLE' || node.querySelector?.('table')) {
                          addTableEventListeners(quill)
                        }
                      }
                    })
                  }
                })
              })
              
              observer.observe(quill.root, {
                childList: true,
                subtree: true
              })
              
              const imgElements = quill.root.querySelectorAll('img')
              console.log('🔧 RichTextEditor: Setting data attributes and size on', imgElements.length, 'images after content load')
              
              // Extract all image tags with data-google-drive-url from the processed HTML
              // Capture the src attribute as well to match images precisely
              const imgRegex = /<img[^>]+src="(data:image\/[^"]+)"[^>]*data-google-drive-url="([^"]+)"[^>]*>/g
              const allMatches = []
              let match
              while ((match = imgRegex.exec(processedHtml)) !== null) {
                allMatches.push({
                  fullTag: match[0],
                  srcData: match[1],
                  googleDriveUrl: match[2]
                })
              }
              
              console.log('🔧 RichTextEditor: Found', allMatches.length, 'images with data-google-drive-url in processed HTML')
              
              const widthRegex = /width="([^"]*)"/
              const heightRegex = /height="([^"]*)"/
              const styleRegex = /style="([^"]*)"/
              
              // Match each DOM image to its corresponding HTML tag by comparing base64 src data
              imgElements.forEach((img, index) => {
                // Check if this image has a base64 src (indicating it was processed)
                if (img.src.startsWith('data:image/')) {
                  // Find the matching HTML tag by comparing src data
                  const matchData = allMatches.find(match => {
                    // Compare the base64 data (strip the data:image prefix for comparison)
                    // We need to normalize both for comparison since they might have slight differences
                    const imgSrc = img.src
                    const matchSrc = match.srcData
                    return imgSrc === matchSrc
                  })
                  
                  if (matchData) {
                    const fullImgTag = matchData.fullTag
                    const googleDriveUrl = matchData.googleDriveUrl
                    
                    // Set the Google Drive URL
                    img.setAttribute('data-google-drive-url', googleDriveUrl)
                    
                    // Extract and apply size attributes
                    const widthMatch = fullImgTag.match(widthRegex)
                    const heightMatch = fullImgTag.match(heightRegex)
                    const styleMatch = fullImgTag.match(styleRegex)
                    
                    if (widthMatch) {
                      img.setAttribute('width', widthMatch[1])
                      img.style.width = widthMatch[1] + 'px'
                    }
                    if (heightMatch) {
                      img.setAttribute('height', heightMatch[1])
                      img.style.height = heightMatch[1] + 'px'
                    }
                    if (styleMatch) {
                      // Apply the style, but ensure our size styles take precedence
                      const existingStyle = styleMatch[1]
                      img.style.cssText = existingStyle
                    }
                    
                    // Ensure max-width is set for responsiveness
                    img.style.maxWidth = '100%'
                    
                    console.log(`🔧 RichTextEditor: Restored image ${index + 1} attributes:`, {
                      googleDriveUrl: googleDriveUrl,
                      width: widthMatch ? widthMatch[1] : 'none',
                      height: heightMatch ? heightMatch[1] : 'none',
                      style: img.style.cssText,
                      actualDisplaySize: `${img.offsetWidth}×${img.offsetHeight}`
                    })
                  } else {
                    console.log(`🔧 RichTextEditor: Image ${index + 1} has no matching data-google-drive-url (likely a drawing or directly inserted base64 image)`)
                  }
                } else {
                  console.log(`🔧 RichTextEditor: Image ${index + 1} skipped (not base64)`)
                }
              })
            }, 100) // Small delay to ensure Quill has finished processing
            
            lastSetValue.current = newHtml // Keep original value for comparison
          } catch (error) {
            console.error('❌ RichTextEditor: Error setting content:', error)
            // Fallback: try to set the original HTML content without processing
            try {
              quill.clipboard.dangerouslyPasteHTML(newHtml, 'silent')
              console.log('🔄 RichTextEditor: Fallback to original HTML content successful')
            } catch (fallbackError) {
              console.error('❌ RichTextEditor: Fallback to original HTML also failed:', fallbackError)
              // Last resort: set as plain text
              quill.setText(newHtml.replace(/<[^>]*>/g, ''))
            }
            lastSetValue.current = newHtml
          }
        }).catch(error => {
          console.error('❌ RichTextEditor: Error processing image references:', error)
          // Fallback: set original HTML content without processing
          try {
            // Clear the editor first
            quill.setContents([], 'silent')
            // Try to set the original content
            quill.clipboard.dangerouslyPasteHTML(newHtml, 'silent')
            console.log('🔄 RichTextEditor: Fallback to unprocessed HTML content successful')
          } catch (fallbackError) {
            console.error('❌ RichTextEditor: Fallback to unprocessed HTML also failed:', fallbackError)
            // Last resort: set as plain text
            quill.setText(newHtml.replace(/<[^>]*>/g, ''))
          }
          lastSetValue.current = newHtml
        })
      }
    }
  }, [value])

  // Handle deselection when clicking outside images
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectedImage && !e.target.closest('.image-with-resize')) {
        // Remove resize handles
        const wrapper = selectedImage.closest('.image-with-resize')
        if (wrapper) {
          const handles = wrapper.querySelector('.resize-handles-container')
          if (handles) handles.remove()
        }
        
        setSelectedImage(null)
        setResizeHandlesVisible(false)
      }
    }
    
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [selectedImage])

  // Auto-attach resize handles to new images
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      // Add safety check to prevent conflicts with Quill's internal observers
      try {
        let hasImageChanges = false
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName === 'IMG' || node.querySelector('img')) {
                hasImageChanges = true
              }
            }
          })
        })
        
        if (hasImageChanges) {
          // Delay to avoid conflicts with Quill's processing
          setTimeout(attachResizeHandlesToImages, 300)
        }
      } catch (error) {
        console.warn('⚠️ RichTextEditor: Error in resize handles MutationObserver:', error)
      }
    })
    
    const editor = quillRef.current?.getEditor()?.root
    if (editor) {
      observer.observe(editor, {
        childList: true,
        subtree: true
      })
    }
    
    return () => observer.disconnect()
  }, [attachResizeHandlesToImages])

  // Initial attachment of resize handles when editor is ready
  useEffect(() => {
    const timer = setTimeout(() => {
      attachResizeHandlesToImages()
    }, 1000) // Increased delay to ensure editor and images are fully loaded
    
    return () => clearTimeout(timer)
  }, [attachResizeHandlesToImages])

  // Handle note link clicks to open linked notes in new tabs
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    
    const handleNoteLinkClick = (e) => {
      const noteLink = e.target.closest('.note-link')
      if (noteLink) {
        e.preventDefault()
        e.stopPropagation()
        
        const noteId = noteLink.getAttribute('data-note-id')
        const noteTitle = noteLink.getAttribute('data-note-title')
        
        if (noteId && noteTitle) {
          console.log(`🔗 RichTextEditor: Opening linked note: ${noteTitle} (${noteId})`)
          
          // Open note in new tab
          openTab({
            type: 'view',
            noteId: noteId,
            title: noteTitle
          })
          
          toast.success(`Opening "${noteTitle}"`, {
            duration: 1500,
            style: {
              background: 'rgba(59, 130, 246, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }
          })
        } else {
          console.warn('⚠️ RichTextEditor: Note link missing data attributes')
          toast.error('Invalid note link')
        }
      }
    }
    
    // Add click listener to editor container
    const editorContainer = quill.container
    editorContainer.addEventListener('click', handleNoteLinkClick)
    
    // Cleanup
    return () => {
      editorContainer.removeEventListener('click', handleNoteLinkClick)
    }
  }, [openTab])

  // React-based @ mention detection (more reliable than Quill modules)
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (!quill) {
      console.log('🔍 RichTextEditor: Quill editor not available yet, retrying...')
      // Retry after a short delay
      const timer = setTimeout(() => {
        const retryQuill = quillRef.current?.getEditor()
        if (retryQuill) {
          console.log('✅ RichTextEditor: Quill editor found on retry, initializing @ detection')
          initializeMentionDetection(retryQuill)
        } else {
          console.warn('⚠️ RichTextEditor: Quill editor still not available after retry')
        }
      }, 500)
      return () => clearTimeout(timer)
    }

    console.log('✅ RichTextEditor: Quill editor available, @ detection DISABLED for performance')
    // initializeMentionDetection(quill) // DISABLED for performance

    function initializeMentionDetection(quill) {
    // Unique identifier for this detection instance
    const detectionId = `mention-detection-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    console.log('🔗 RichTextEditor: Initializing mention detection with ID:', detectionId)

    let mentionButton = null
    let isShowingButton = false
    let currentMentionIndex = null
    let hideTimeout = null

    const showMentionButton = (atIndex, partialText = '') => {
      if (isShowingButton && currentMentionIndex === atIndex) {
        // Reset timeout
        if (hideTimeout) clearTimeout(hideTimeout)
        hideTimeout = setTimeout(hideMentionButton, 3000)
        return
      }

      hideMentionButton()
      currentMentionIndex = atIndex

      // Detect mobile and theme
      const isMobile = window.innerWidth < 768
      const isAmoled = document.documentElement.classList.contains('amoled')
      const isDark = document.documentElement.classList.contains('dark')

      // Get cursor position
      const bounds = quill.getBounds(atIndex + 1)

      // Create floating button
      mentionButton = document.createElement('button')
      mentionButton.className = 'note-mention-button'
      mentionButton.innerHTML = `
        <svg class="link-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M9.16488 17.6505C8.92513 17.8743 8.73958 18.0241 8.54996 18.1336C7.62175 18.6695 6.47816 18.6695 5.54996 18.1336C5.20791 17.9361 4.87912 17.6073 4.22153 16.9498C3.56394 16.2922 3.23514 15.9634 3.03767 15.6213C2.50177 14.6931 2.50177 13.5495 3.03767 12.6213C3.23514 12.2793 3.56394 11.9505 4.22153 11.2929L7.04996 8.46448C7.70755 7.80689 8.03634 7.47809 8.37838 7.28062C9.30659 6.74472 10.4502 6.74472 11.3784 7.28061C11.7204 7.47809 12.0492 7.80689 12.7068 8.46448C13.3644 9.12207 13.6932 9.45086 13.8907 9.7929C14.4266 10.7211 14.4266 11.8647 13.8907 12.7929C13.7812 12.9825 13.6314 13.1681 13.4075 13.4078M10.5919 10.5922C10.368 10.8319 10.2182 11.0175 10.1087 11.2071C9.57284 12.1353 9.57284 13.2789 10.1087 14.2071C10.3062 14.5492 10.635 14.878 11.2926 15.5355C11.9502 16.1931 12.279 16.5219 12.621 16.7194C13.5492 17.2553 14.6928 17.2553 15.621 16.7194C15.9631 16.5219 16.2919 16.1931 16.9495 15.5355L19.7779 12.7071C20.4355 12.0495 20.7643 11.7207 20.9617 11.3787C21.4976 10.4505 21.4976 9.30689 20.9617 8.37869C20.7643 8.03665 20.4355 7.70785 19.7779 7.05026C19.1203 6.39267 18.7915 6.06388 18.4495 5.8664C17.5212 5.3305 16.3777 5.3305 15.4495 5.8664C15.2598 5.97588 15.0743 6.12571 14.8345 6.34955"></path>
        </svg>
        <span class="button-text">Link Note</span>
      `
      mentionButton.title = partialText ? `Link note (searching: "${partialText}")` : 'Link to note'
      mentionButton.type = 'button'

      // Mobile-optimized sizing
      const buttonSize = isMobile ? 'auto' : 'auto' // Let content determine size
      const buttonHeight = isMobile ? 44 : 32
      const offsetY = isMobile ? 50 : 35

      // Calculate position (estimate button width)
      const estimatedButtonWidth = isMobile ? 100 : 100 // Consistent width for "Link Note" text
      const leftPos = Math.max(10, Math.min(bounds.left, window.innerWidth - estimatedButtonWidth - 10))
      const topPos = Math.max(10, bounds.top - offsetY)

      // Theme-aware colors
      let buttonStyles = ''
      if (isAmoled) {
        buttonStyles = `
          background: linear-gradient(135deg, #000000 0%, #111111 100%);
          border: 2px solid #374151;
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(255,255,255,0.1), 0 0 0 1px rgba(255,255,255,0.05);
        `
      } else if (isDark) {
        buttonStyles = `
          background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
          border: 2px solid #4b5563;
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1);
        `
      } else {
        buttonStyles = `
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border: 2px solid #3b82f6;
          color: #1f2937;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 1px rgba(59,130,246,0.1);
        `
      }

      mentionButton.style.cssText = `
        position: absolute;
        left: ${leftPos}px;
        top: ${topPos}px;
        height: ${buttonHeight}px;
        padding: ${isMobile ? '8px 12px' : '6px 10px'};
        border-radius: ${buttonHeight / 2}px;
        cursor: pointer;
        z-index: 1000;
        font-size: ${isMobile ? 14 : 12}px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: ${isMobile ? 6 : 4}px;
        transition: all 0.2s ease;
        touch-action: manipulation;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        outline: none;
        user-select: none;
        white-space: nowrap;
        ${buttonStyles}
      `

      // Add interactions with null checks
      if (isMobile) {
        mentionButton.addEventListener('touchstart', (e) => {
          e.preventDefault()
          if (navigator.vibrate) navigator.vibrate(50)
          if (mentionButton && mentionButton.style) {
            mentionButton.style.transform = 'scale(0.95)'
          }
        })
        mentionButton.addEventListener('touchend', () => {
          if (mentionButton && mentionButton.style) {
            mentionButton.style.transform = 'scale(1)'
          }
        })
      } else {
        mentionButton.addEventListener('mouseenter', () => {
          if (mentionButton && mentionButton.style) {
            mentionButton.style.transform = 'scale(1.05)'
          }
        })
        mentionButton.addEventListener('mouseleave', () => {
          if (mentionButton && mentionButton.style) {
            mentionButton.style.transform = 'scale(1)'
          }
        })
      }

      // Click handler with debugging
      const handleButtonClick = (e) => {
        console.log(`🔗 RichTextEditor: [${detectionId}] Mention button clicked!`)
        console.log('🔗 RichTextEditor: Event type:', e.type)
        console.log('🔗 RichTextEditor: Is mobile:', window.innerWidth < 768)
        
        e.preventDefault()
        e.stopPropagation()
        
        if (currentMentionIndex !== null) {
          const range = quill.getSelection()
          if (range) {
            const text = quill.getText(currentMentionIndex, range.index - currentMentionIndex + 1)
            const atMatch = text.match(/@(\w*)/)
            const partialText = atMatch ? atMatch[1] : ''
            console.log('🔗 RichTextEditor: Calling handleMentionButtonClick with:', currentMentionIndex, partialText)
            handleMentionButtonClick(currentMentionIndex, partialText)
          } else {
            console.warn('🔗 RichTextEditor: No selection range available when button clicked')
          }
        } else {
          console.warn('🔗 RichTextEditor: No currentMentionIndex when button clicked')
        }
        hideMentionButton()
      }
      
      mentionButton.addEventListener('click', handleButtonClick)
      
      // For mobile, also add touchend as backup
      if (isMobile) {
        mentionButton.addEventListener('touchend', (e) => {
          console.log('🔗 RichTextEditor: Mention button touch end')
          // Small delay to prevent double firing
          setTimeout(() => handleButtonClick(e), 50)
        })
      }

      // Add to editor
      quill.container.style.position = 'relative'
      quill.container.appendChild(mentionButton)
      isShowingButton = true

      // Auto-hide
      hideTimeout = setTimeout(hideMentionButton, isMobile ? 5000 : 3000)
      
      console.log(`🔗 RichTextEditor: [${detectionId}] Showing mention button at position ${atIndex}${partialText ? ` (partial: "${partialText}")` : ''}`)
    }

    const hideMentionButton = () => {
      if (mentionButton) {
        mentionButton.remove()
        mentionButton = null
      }
      if (hideTimeout) {
        clearTimeout(hideTimeout)
        hideTimeout = null
      }
      isShowingButton = false
      currentMentionIndex = null
    }

    const handleTextChange = (delta, oldDelta, source) => {
      console.log('🔍 RichTextEditor: Text change detected (NEW LOGIC v2), source:', source)
      
      if (source !== 'user') {
        console.log('🔍 RichTextEditor: Ignoring non-user text change')
        return
      }

      // Check if in sensitive context
      if (isSensitiveContext) {
        console.log('🔒 RichTextEditor: @ mentions disabled in sensitive context')
        hideMentionButton()
        return
      }

      const range = quill.getSelection()
      if (!range) {
        console.log('🔍 RichTextEditor: No selection range available')
        return
      }

      console.log('🔍 RichTextEditor: Current cursor position:', range.index)

      // Get text around cursor
      const startIndex = Math.max(0, range.index - 20)
      const text = quill.getText(startIndex, 20)
      console.log('🔍 RichTextEditor: Text around cursor:', JSON.stringify(text))
      
      const lastAtIndex = text.lastIndexOf('@')
      console.log('🔍 RichTextEditor: Last @ index in text:', lastAtIndex)

      if (lastAtIndex !== -1) {
        const actualAtIndex = startIndex + lastAtIndex
        const textAfterAt = text.substring(lastAtIndex + 1)
        console.log('🔍 RichTextEditor: Text after @:', JSON.stringify(textAfterAt))

        // Simpler approach: Check if cursor is close to @ and text after @ is valid
        const cursorDistanceFromAt = range.index - actualAtIndex
        console.log('🔍 RichTextEditor: Cursor distance from @:', cursorDistanceFromAt)
        
        // Show button if cursor is 1-10 characters after @ and text is valid
        if (cursorDistanceFromAt >= 1 && cursorDistanceFromAt <= 10) {
          // Get only the text between @ and cursor (excluding the @ itself)
          const textBetweenLength = cursorDistanceFromAt - 1
          const textBetween = textBetweenLength > 0 ? quill.getText(actualAtIndex + 1, textBetweenLength) : ''
          console.log('🔍 RichTextEditor: Text between @ and cursor (length:', textBetweenLength, '):', JSON.stringify(textBetween))
          
          // Show button if text between is empty or only word characters (no spaces, punctuation, newlines)
          if (textBetween === '' || /^\w*$/.test(textBetween)) {
            console.log('🎯 RichTextEditor: @ detected! Showing mention button')
            showMentionButton(actualAtIndex, textBetween)
            return
          } else {
            console.log('🔍 RichTextEditor: Invalid text between @ and cursor:', JSON.stringify(textBetween))
          }
        } else {
          console.log('🔍 RichTextEditor: Cursor too far from @ symbol:', cursorDistanceFromAt)
        }
      }

      console.log('🔍 RichTextEditor: No @ context found, hiding button')
      hideMentionButton()
    }

    const handleSelectionChange = (range, oldRange, source) => {
      if (!range) {
        hideMentionButton()
        return
      }

      // Hide if cursor moved away from @ context
      if (isShowingButton && currentMentionIndex !== null) {
        const text = quill.getText(currentMentionIndex, range.index - currentMentionIndex + 1)
        if (!text.includes('@') || range.index < currentMentionIndex) {
          hideMentionButton()
        }
      }
    }

    // Add event listeners
    console.log('🔗 RichTextEditor: Adding @ detection event listeners')
    quill.on('text-change', handleTextChange)
    quill.on('selection-change', handleSelectionChange)

    // Cleanup function for this specific quill instance
    return () => {
      console.log('🧹 RichTextEditor: Cleaning up @ detection listeners')
      hideMentionButton()
      quill.off('text-change', handleTextChange)
      quill.off('selection-change', handleSelectionChange)
    }
    
    } // End of initializeMentionDetection function
  }, [isSensitiveContext, handleMentionButtonClick])

  // Quill formats configuration
  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'color', 'background', 'align',
    'link', 'image', 'blockquote', 'code-block',
    'noteLink', // Custom format for note links
    'table' // Simple table format
  ]

  // Conditional styles based on fullscreen mode
  const quillStyle = isFullscreen ? {
    height: '100%',
    marginBottom: '0',
    display: 'flex',
    flexDirection: 'column'
  } : {
    height: '550px', // Increased from 500px for better editing experience
    marginBottom: '42px'
  }

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    processContentForSave: (content) => processContentForSave(content)
  }))

  return (
    <div className={`rich-text-editor ${className}`}>
      <EmojiPicker
        isOpen={isEmojiPickerOpen}
        onEmojiSelect={handleEmojiSelect}
        onClose={() => setIsEmojiPickerOpen(false)}
      />

      {/* AI Search Modal - Using Portal to escape overflow containers */}
      {isAIModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {showAIResponse ? 'AI Response' : 'AI Search'}
              </h3>
                             <button
                 type="button"
                 onClick={handleCloseAIModal}
                 className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                 title="Close"
               >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              {!showAIResponse ? (
                // Query Input State
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ask AI to help with your content:
                    </label>
                    <textarea
                      value={aiQuery}
                      onChange={(e) => setAiQuery(e.target.value)}
                      onKeyPress={handleAIKeyPress}
                      placeholder="e.g., Write a summary about renewable energy in less than 50 words..."
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={4}
                      disabled={isLoadingAI}
                    />
                  </div>
                                     <div className="flex justify-end">
                     <button
                       type="button"
                       onClick={handleAISearch}
                       disabled={!aiQuery.trim() || isLoadingAI}
                       className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
                     >
                      {isLoadingAI ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <span>Searching...</span>
                        </>
                      ) : (
                        <>
                          <span>→</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                // AI Response State
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      AI Generated Content:
                    </label>
                    <div className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 max-h-60 overflow-y-auto">
                      <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap text-sm leading-relaxed">
                        {aiResponse}
                      </p>
                    </div>
                  </div>
                                     <div className="flex justify-end space-x-3">
                     <button
                       type="button"
                       onClick={() => setShowAIResponse(false)}
                       className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                     >
                       Try Again
                     </button>
                     <button
                       type="button"
                       onClick={handlePasteAIResponse}
                       className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                     >
                       Paste in Note
                     </button>
                   </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Drawing Canvas Modal */}
      <DrawingModal
        isOpen={isDrawingModalOpen}
        onClose={handleCloseDrawingModal}
        onDone={handleDrawingDone}
        backgroundImage={editingImage}
        mode={editingImage ? 'edit' : 'create'}
        noteTitle={noteTitle}
      />

      {/* Audio Recording Modal */}
      <AudioRecordingModal
        isOpen={isAudioModalOpen}
        onClose={handleCloseAudioModal}
        onDone={handleAudioDone}
      />

      {/* Image Resize Modal */}
      <ImageResizeModal
        isOpen={isImageResizeModalOpen}
        onClose={handleCloseImageResizeModal}
        onApply={handleImageResizeApply}
        imageElement={resizeImageData?.element}
        currentWidth={resizeImageData?.currentWidth || 300}
        currentHeight={resizeImageData?.currentHeight || 200}
      />

      {/* Color Picker Modal */}
      <ColorPickerModal
        isOpen={isColorPickerOpen}
        onClose={handleCloseColorPicker}
        onColorSelect={handleColorSelect}
        type={colorPickerType}
        title={colorPickerType === 'text' ? 'Text Color' : 'Background Color'}
      />

      {/* Picker Modal for headers and alignment */}
      <PickerModal
        isOpen={isPickerModalOpen}
        onClose={handleClosePicker}
        onSelect={handlePickerSelect}
        type={pickerType}
        title={pickerType === 'header' ? 'Heading Style' : 'Text Alignment'}
      />

      {/* Note Link Modal for @ mentions */}
      <NoteLinkModal
        isOpen={isNoteLinkModalOpen}
        onClose={handleNoteLinkModalClose}
        onSelectNote={handleNoteSelected}
        searchTerm={mentionPartialText}
        currentNoteId={currentNoteId}
      />
      
      <ReactQuill
        ref={(quillInstance) => {
          // Set both refs to ensure compatibility
          quillRef.current = quillInstance
          if (ref) {
            if (typeof ref === 'function') {
              ref(quillInstance)
            } else {
              ref.current = quillInstance
            }
          }
        }}
        theme="snow"
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        readOnly={disabled}
        className={`${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${isOverLimit ? 'character-limit-exceeded' : ''}`}
        style={quillStyle}
      />
      
      {/* Character Counter */}
      {showCharacterCount && maxLength && !isFullscreen && (
        <div className={`character-counter ${isNearLimit ? 'near-limit' : ''} ${isOverLimit ? 'over-limit' : ''}`}>
          <span className="character-count">
            {currentLength.toLocaleString()} / {maxLength.toLocaleString()}
          </span>
          <span className="character-label">characters</span>
          {isOverLimit && (
            <span className="limit-exceeded-warning">
              ⚠️ Content exceeds limit and cannot be saved
            </span>
          )}
        </div>
      )}
      {showCharacterCount && !maxLength && !isFullscreen && (
        <div className="character-counter">
          <span className="character-count">
            {currentLength.toLocaleString()}
          </span>
          <span className="character-label">characters</span>
        </div>
      )}
      {showCharacterCount && maxLength && isFullscreen && (
        <div className={`character-counter-fullscreen ${isNearLimit ? 'near-limit' : ''} ${isOverLimit ? 'over-limit' : ''}`}>
          {currentLength.toLocaleString()} / {maxLength.toLocaleString()}
          {isOverLimit && <span className="warning-icon">⚠️</span>}
        </div>
      )}
      {showCharacterCount && !maxLength && isFullscreen && (
        <div className="character-counter-fullscreen">
          {currentLength.toLocaleString()} characters
        </div>
      )}
    </div>
  )
})

RichTextEditor.displayName = 'RichTextEditor'

export default RichTextEditor 